// frontend/src/pages/AccountPage/AccountPage.js

import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext';
import { useUnsavedChanges, UnsavedChangesProvider } from '../../contexts/UnsavedChangesContext';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ApiKeysManager from '../../components/ApiKeysManager/ApiKeysManager';
import { LANGUAGES } from '../../components/LanguageSwitcher/LanguageSwitcher';
import { 
    Box, 
    Typography, 
    TextField, 
    Button, 
    Alert, 
    CircularProgress, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogContentText, 
    DialogTitle, 
    InputAdornment, 
    IconButton, 
    Switch, 
    FormControlLabel, 
    FormControl,
    Select,
    MenuItem,
    FormGroup,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    Divider,
    Slider,
    Autocomplete,
    Chip,
    Collapse,
    Backdrop,
    ListItemText,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import SecurityIcon from '@mui/icons-material/Security';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import GTranslateIcon from '@mui/icons-material/GTranslate';
import validator from 'validator';
import useUnsavedChangesWarning from './useUnsavedChangesWarning';
import './AccountPage.css';
import '../../components/HistoryFilters/HistoryFilters.css';
import { PLATFORM_ICONS, getPlatformDisplayName } from '../../components/PlatformUtils/PlatformUtils.jsx';

const DEFAULT_PREFS = {
    default_content_type: 'image',
    displayed_content_types: ['image', 'text', 'video', 'audio'],
    default_tasks: [],
    displayed_tasks: [],
    enabled_meta_tasks: ['digital_integrity_score'],
    default_regions: true,
    display_regions: true,
    default_explanation: true,
    display_explanation: true,
    default_qr_label: 'VERIFIED',
    default_language: 'en'
};
const ORDERED_CONTENT_TYPES = ['image', 'text', 'video', 'audio'];

export const ALL_DASHBOARD_PLATFORMS = [
    'facebook',
    'google',
    'instagram',
    'tiktok',
    'twitter',
    'youtube',
];

// --- Threat Radar States ---
const TR_SYSTEM_DEFAULTS = {
    default_keywords: [],
    default_content_types: ['image', 'video', 'audio', 'text'],
    default_platforms: ALL_DASHBOARD_PLATFORMS,
    default_tasks: ['fake_news_detection', 'scam_detection', 'brand_safety_analysis'],
    default_score_range: [70, 100],
    default_source_filter: ['scraped', 'public', 'local'],
    default_date_range: 'last_7_days',
    watermarked_only: false,
    active_monitoring: false,
    monitoring_platforms: ['facebook', 'google', 'instagram', 'tiktok', 'twitter', 'youtube'],
    monitoring_watermarked_only: false,
    monitoring_tasks: ['fake_news_detection', 'scam_detection', 'brand_safety_analysis'],
    monitoring_cadence: 'daily',
    monitoring_max_posts: 50,
    monitoring_weights: {},
    monitoring_sort_methods: {}, // Format: { "twitter": "latest", "youtube": "popular" }
    email_alerts_enabled: false,
    email_cadence: 'weekly',
    email_include_duplicates: false,
    automated_takedowns: false,
    business_profile: '',
    last_suggested_profile_hash: null,
    last_suggested_output: null
};

// Define which platforms actually support POPULAR vs LATEST sorting via Apify
const SORT_SUPPORTED_PLATFORMS = {
    'facebook': false,
    'google': false,
    'instagram': false,
    'tiktok': false,
    'twitter': true,
    'youtube': true,
};

const VALID_DATE_RANGES = ['all_time', 'last_30_days', 'last_7_days', 'last_24_hours'];

const getMergedTrPrefs = (userTrPrefs) => {
    const base = { ...TR_SYSTEM_DEFAULTS, ...(userTrPrefs || {}) };
    
    // Ensure displayed_tasks exists for older/new accounts
    if (!base.displayed_tasks) {
        base.displayed_tasks = base.default_tasks || TR_SYSTEM_DEFAULTS.default_tasks;
    }

    // Clean up default_tasks to only include tasks that are actually in displayed_tasks
    // This prevents legacy tasks from remaining active in the DB if they were hidden from the UI
    if (base.default_tasks && base.displayed_tasks) {
        base.default_tasks = base.default_tasks.filter(t => base.displayed_tasks.includes(t));
    }

    // Force the fallback if the date range is empty OR if it's an old legacy value
    if (!base.default_date_range || !VALID_DATE_RANGES.includes(base.default_date_range)) {
        base.default_date_range = 'last_7_days';
    }

    // Ensure new users get the 3-way toggle if it somehow doesn't exist
    if (!base.default_source_filter || base.default_source_filter.length === 0) {
        base.default_source_filter = ['scraped', 'public', 'local'];
    }

    // Migrate old 'none' cadence to the new boolean switch
    if (base.email_cadence === 'none') {
        base.email_alerts_enabled = false;
        base.email_cadence = 'weekly';
    }

    if (!base.monitoring_sort_methods) {
        base.monitoring_sort_methods = {};
    }

    return base;
};

// Helper to safely merge user preferences with YAML defaults without overriding intentionally empty arrays
const getMergedPrefs = (userPrefs, tasksCfg) => {
    const base = { ...DEFAULT_PREFS, ...(userPrefs || {}) };
    if (tasksCfg) {
        const allStandardTasks = Object.keys(tasksCfg).filter(k => !tasksCfg[k].is_meta_task);
        const yamlDefaultTasks = allStandardTasks.filter(k => tasksCfg[k].default_on_ui !== false);
        const allMetaTasks = Object.keys(tasksCfg).filter(k => tasksCfg[k].is_meta_task);
        const yamlDefaultMetaTasks = allMetaTasks.filter(k => tasksCfg[k].default_on_ui === true);

        if (!userPrefs) {
            base.displayed_tasks = allStandardTasks;
            base.default_tasks = yamlDefaultTasks;
            base.enabled_meta_tasks = yamlDefaultMetaTasks;
        } else {
            // Only apply YAML defaults if the key is completely missing from user prefs
            if (userPrefs.displayed_tasks === undefined) base.displayed_tasks = allStandardTasks;
            if (userPrefs.default_tasks === undefined) base.default_tasks = yamlDefaultTasks;
            if (userPrefs.enabled_meta_tasks === undefined) base.enabled_meta_tasks = yamlDefaultMetaTasks;
        }
    }
    return base;
};

const AccountPageContent = () => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, updateUserSetting, fetchWithAuth, setUser } = useContext(AuthContext);
    const { setIsFaqModalOpen, openDocViewer, setIsOnboardingModalOpen, setIsPageBlurred } = useContext(UIContext);
    const { setIsDirty } = useUnsavedChanges();
    const prefsSectionRef = useRef(null);
    const threatRadarRef = useRef(null);
    const saveButtonRef = useRef(null);
    const hasScrolledRef = useRef(false);

    // Determines if the user is running the app locally for testing
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // Setup Top-Level User Settings
    const [isAdToolsEnabled, setIsAdToolsEnabled] = useState(!!user?.show_ad_tools_by_default);
    const [currency, setCurrency] = useState(user?.preferred_currency || 'USD');

    // --- Threat Radar Logic ---
    // Format: { "Keyword1": "checking", "Keyword2": "en", "Keyword3": "it" }
    const [trKeywordLangStatus, setTrKeywordLangStatus] = useState({});

    const verifyTrKeywordLanguage = async (keywordsToCheck) => {
        if (!keywordsToCheck || keywordsToCheck.length === 0) return;

        setTrKeywordLangStatus(prev => {
            const newState = { ...prev };
            keywordsToCheck.forEach(kw => { newState[kw] = 'checking'; });
            return newState;
        });

        try {
            const response = await fetchWithAuth('/api/threat-radar/check-language', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ keywords: keywordsToCheck })
            });

            if (response.ok) {
                const results = await response.json();
                setTrKeywordLangStatus(prev => ({ ...prev, ...results }));
            } else {
                setTrKeywordLangStatus(prev => {
                    const newState = { ...prev };
                    keywordsToCheck.forEach(kw => { newState[kw] = 'en'; });
                    return newState;
                });
            }
        } catch (error) {
            setTrKeywordLangStatus(prev => {
                const newState = { ...prev };
                keywordsToCheck.forEach(kw => { newState[kw] = 'en'; });
                return newState;
            });
        }
    };

    const [trPrefs, setTrPrefs] = useState(() => getMergedTrPrefs(user?.threat_radar_preferences));
    const [activeScoreThumb, setActiveScoreThumb] = useState(null);
    const scoreTimeoutRef = useRef(null);
    const [isMaxPostsHighlighted, setIsMaxPostsHighlighted] = useState(false);
    const maxPostsTimeoutRef = useRef(null);
    const [trKeywordInput, setTrKeywordInput] = useState('');
    const MAX_TR_KEYWORDS = 10;

    // --- Commit pending Threat Radar keyword gracefully on Blur ---
    const handleCommitPendingTrKeyword = () => {
        if (!trKeywordInput) return;
        const val = trKeywordInput.trim();
        if (!val) return;

        // Prevent duplicates
        if (trPrefs.default_keywords.some(k => k.toLowerCase() === val.toLowerCase())) {
            setTrKeywordInput('');
            return;
        }

        const newArr = [...trPrefs.default_keywords, val];
        const lowercasedUnique = [...new Set(newArr.map(k => k.trim().toLowerCase()))].filter(k => k !== '');
        const limited = lowercasedUnique.slice(0, MAX_TR_KEYWORDS);
        
        // Only verify truly new keywords, not the ones that were already saved
        const addedKeywords = limited.filter(kw => !trPrefs.default_keywords.includes(kw));
        
        setTrPrefs(prev => ({ ...prev, default_keywords: limited }));
        setTrKeywordInput(''); 
        
        if (addedKeywords.length > 0) {
            verifyTrKeywordLanguage(addedKeywords);
        }
    };
    
    // Setup Analysis Preferences
    const [analysisPrefs, setAnalysisPrefs] = useState(() => getMergedPrefs(user?.analysis_preferences, null));   
    const [isEditingName, setIsEditingName] = useState(false);
    const [newFullName, setNewFullName] = useState(user?.full_name || '');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const isEmailUser = !user?.auth_provider || user?.auth_provider === 'email';
    const confirmKeyword = t('accountPage.deleteModal.confirmKeyword', { defaultValue: 'delete' });
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [passwordError, setPasswordError] = useState('');
    const [isManualUnsavedDialogOpen, setIsManualUnsavedDialogOpen] = useState(false);
    const isTrapActive = useRef(false);

    // --- Threat Radar Password Modal State ---
    const [isRadarPasswordModalOpen, setIsRadarPasswordModalOpen] = useState(false);
    const [radarPasswordInput, setRadarPasswordInput] = useState('');
    const [radarPasswordError, setRadarPasswordError] = useState('');
    const [isVerifyingRadarPassword, setIsVerifyingRadarPassword] = useState(false);

    const SUPPORTED_CURRENCIES = ["AUD", "CAD", "CHF", "CNH", "EUR", "GBP", "JPY", "USD"];

    const CURRENCY_SYMBOLS = {
        AUD: 'A$', CAD: 'C$', CHF: 'Fr', CNH: '¥', EUR: '€', GBP: '£', JPY: '¥', USD: '$'
    };

    // --- AI Keyword Assistant State ---
    const [isKeywordModalOpen, setIsKeywordModalOpen] = useState(false);
    const [isGeneratingKeywords, setIsGeneratingKeywords] = useState(false);
    const [showSlowLoadingMsg, setShowSlowLoadingMsg] = useState(false);
    const [assistantError, setAssistantError] = useState('');
    const [suggestedAiKeywords, setSuggestedAiKeywords] = useState([]);
    const [selectedAiKeywords, setSelectedAiKeywords] = useState([]);
    const [aiKeywordReasons, setAiKeywordReasons] = useState({});
    const [suggestedAiTasks, setSuggestedAiTasks] = useState([]);
    const [selectedAiTasks, setSelectedAiTasks] = useState([]);
    const [aiTaskReasons, setAiTaskReasons] = useState({});

    const availableSlots = Math.max(0, MAX_TR_KEYWORDS - trPrefs.default_keywords.length);
    const hasNoKeywords = trPrefs.default_keywords.length === 0;
    const [isHoveringDisabledSwitch, setIsHoveringDisabledSwitch] = useState(false);

    const handleGenerateKeywords = async () => {
        const cleanPrompt = (trPrefs.business_profile || '').trim();
        if (!cleanPrompt) return;

        setIsGeneratingKeywords(true);
        setShowSlowLoadingMsg(false);
        setAssistantError('');
        
        // --- PRE-FLIGHT LANGUAGE CHECK ---
        try {
            const langRes = await fetchWithAuth('/api/threat-radar/check-language', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ keywords: [cleanPrompt] })
            });
            
            if (langRes.ok) {
                const langData = await langRes.json();
                const detectedLang = langData[cleanPrompt];
                
                if (detectedLang && detectedLang !== 'en' && detectedLang !== 'checking') {
                    let langName = detectedLang.toUpperCase();
                    try {
                        const displayNames = new Intl.DisplayNames([i18n.language || 'en'], { type: 'language' });
                        langName = displayNames.of(detectedLang);
                    } catch (e) {} // Fallback to raw code
                    
                    setAssistantError(t('accountPage.threatRadarPrefs.keywordAssistant.languageError', { 
                        lang: langName, 
                        defaultValue: `The AI assistant requires English instructions. Detected language: ${langName}` 
                    }));
                    setIsGeneratingKeywords(false);
                    return; // Abort process
                }
            }
        } catch (e) {
            console.warn("Pre-flight language check failed, continuing...", e);
        }
        
        // Cache hits take <100ms. LLMs take 10+ seconds. 
        // This timer ensures the message ONLY appears if the LLM is actually running.
        const loadingTimer = setTimeout(() => setShowSlowLoadingMsg(true), 500);

        setSuggestedAiKeywords([]);
        setSelectedAiKeywords([]);
        setSuggestedAiTasks([]);
        setSelectedAiTasks([]);

        // Extract non-meta tasks to send to the AI
        const availableTasks = tasksConfig ? Object.keys(tasksConfig).filter(k => !tasksConfig[k].is_meta_task) : [];

        try {
            const response = await fetchWithAuth('/api/threat-radar/suggest-keywords', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt: cleanPrompt, available_tasks: availableTasks })
            });

            const data = await response.json();
            if (response.ok && data.keywords && data.tasks) {
                
                const cleanedKeywords = [];
                const kwReasons = {};
                
                data.keywords.forEach(item => {
                    const kw = item.keyword?.trim().toLowerCase();
                    if (kw && !cleanedKeywords.includes(kw)) {
                        cleanedKeywords.push(kw);
                        kwReasons[kw] = item.reason;
                    }
                });

                const validSuggestedTasks = [];
                const tReasons = {};
                
                data.tasks.forEach(item => {
                    const tk = item.task?.trim();
                    if (availableTasks.includes(tk) && !validSuggestedTasks.includes(tk)) {
                        validSuggestedTasks.push(tk);
                        tReasons[tk] = item.reason;
                    }
                });

                setSuggestedAiKeywords(cleanedKeywords);
                setAiKeywordReasons(kwReasons);
                
                setSuggestedAiTasks(validSuggestedTasks);
                setAiTaskReasons(tReasons);

                // Smart Pre-Selection Logic:
                // Filter out already-tracked keywords from the suggestions pool completely
                const currentlyTrackedKws = trPrefs.default_keywords.map(k => k.toLowerCase());
                const newSuggestionsOnly = cleanedKeywords.filter(k => !currentlyTrackedKws.includes(k));
                
                setSuggestedAiKeywords(newSuggestionsOnly);
                setSelectedAiKeywords(newSuggestionsOnly.slice(0, availableSlots));
                
                // Select all valid suggested tasks (they merge safely on apply)
                setSelectedAiTasks(validSuggestedTasks);

                // --- KEEP CACHE IN SYNC SO REACT DOESN'T OVERWRITE DB ---
                const updatedCachePrefs = {
                    business_profile: cleanPrompt,
                    last_suggested_profile_hash: data.cache_hash || trPrefs.last_suggested_profile_hash,
                    last_suggested_output: { keywords: data.keywords, tasks: data.tasks }
                };

                setTrPrefs(prev => ({
                    ...prev,
                    ...updatedCachePrefs
                }));

                // Instantly sync into the global User context so originalTrPrefs updates 
                // and the "Unsaved Changes" banner doesn't spuriously appear on dismissal
                setUser(prev => ({
                    ...prev,
                    threat_radar_preferences: {
                        ...(prev.threat_radar_preferences || {}),
                        ...updatedCachePrefs
                    }
                }));

            } else {
                setNotification({ message: data.error || t('accountPage.preferences.saveFailure'), type: 'error' });
            }
        } catch (err) {
            setNotification({ message: t('accountPage.threatRadarPrefs.keywordAssistant.networkError', 'Network error during keyword generation'), type: 'error' });
        } finally {
            clearTimeout(loadingTimer);
            setIsGeneratingKeywords(false);
            setShowSlowLoadingMsg(false);
        }
    };

    const handleApplySuggestedKeywords = () => {
        if (selectedAiKeywords.length === 0 && selectedAiTasks.length === 0) return;

        const newKeywordsArr = [...trPrefs.default_keywords, ...selectedAiKeywords];
        const lowercasedUniqueKeywords = [...new Set(newKeywordsArr.map(k => k.trim().toLowerCase()))].filter(k => k !== '');
        const limitedKeywords = lowercasedUniqueKeywords.slice(0, MAX_TR_KEYWORDS);
        
        // Remove tasks that were suggested (so we respect the user's un-checking of them), 
        // then append the ones the user actually checked. 
        // Tasks that were NOT suggested by the AI retain their previous state.
        const currentMonitoring = trPrefs.monitoring_tasks || [];
        const keptMonitoring = currentMonitoring.filter(t => !suggestedAiTasks.includes(t));
        const newMonitoringTasksArr = [...new Set([...keptMonitoring, ...selectedAiTasks])];

        const currentDefault = trPrefs.default_tasks || [];
        const keptDefault = currentDefault.filter(t => !suggestedAiTasks.includes(t));
        const newDefaultTasksArr = [...new Set([...keptDefault, ...selectedAiTasks])];

        // Ensure newly recommended tasks are also visible in the displayed_tasks list
        const currentDisplayed = trPrefs.displayed_tasks || [];
        const newDisplayedTasksArr = [...new Set([...currentDisplayed, ...selectedAiTasks])];

        setTrPrefs(prev => ({ 
            ...prev, 
            default_keywords: limitedKeywords,
            monitoring_tasks: newMonitoringTasksArr,
            default_tasks: newDefaultTasksArr,
            displayed_tasks: newDisplayedTasksArr
        }));

        // Automatically expand the Threat Radar accordion and the Proactive Monitoring section
        setExpandedPanels(prev => ({ ...prev, panel5: true }));
        setIsMonitoringExpanded(true);

        // Scroll to the proactive monitoring switch to draw attention to it
        setTimeout(() => {
            if (proactiveSwitchRef.current) {
                proactiveSwitchRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setHighlightProactiveSwitch(true);
            }
        }, 300);

        // Language verification check
        const addedKeywords = limitedKeywords.filter(kw => !trPrefs.default_keywords.includes(kw));
        if (addedKeywords.length > 0) verifyTrKeywordLanguage(addedKeywords);

        setIsKeywordModalOpen(false);
        setSuggestedAiKeywords([]);
        setSelectedAiKeywords([]);
        setSuggestedAiTasks([]);
        setSelectedAiTasks([]);
    };

    const toggleTaskSelection = (taskKey) => {
        setSelectedAiTasks(prev => 
            prev.includes(taskKey) ? prev.filter(t => t !== taskKey) : [...prev, taskKey]
        );
    };

    const toggleKeywordSelection = (kw) => {
        setSelectedAiKeywords(prev => {
            if (prev.includes(kw)) {
                return prev.filter(item => item !== kw);
            }
            // Point 9: Guard against exceeding capacity
            if (prev.length >= availableSlots) {
                return prev; // Block selection
            }
            return [...prev, kw];
        });
    };
    // Fetch Global Threat Radar Config
    const { data: radarConfigData } = useQuery({
        queryKey: ['radarConfig'],
        queryFn: async () => {
            const res = await fetchWithAuth('/api/threat-radar/config');
            if (!res.ok) throw new Error('Failed to load radar config');
            return res.json();
        },
        staleTime: Infinity,
    });

    const radarGlobalPlatforms = useMemo(() => {
        const rawPlatforms = radarConfigData?.platforms || ['facebook', 'google', 'instagram', 'tiktok', 'twitter', 'youtube'];
        return [...rawPlatforms].sort((a, b) => 
            getPlatformDisplayName(a, t).localeCompare(getPlatformDisplayName(b, t))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [radarConfigData, i18n.language]);

    const sortedDashboardPlatforms = useMemo(() => {
        return [...ALL_DASHBOARD_PLATFORMS].sort((a, b) => 
            getPlatformDisplayName(a, t).localeCompare(getPlatformDisplayName(b, t))
        );
    }, [t, i18n.language]);

    const radarGlobalMaxPosts = useMemo(() => radarConfigData?.max_posts_per_scrape || 50, [radarConfigData]);

    // Cache the Tasks Config
    const { data: tasksConfigData } = useQuery({
        queryKey: ['tasksConfig'],
        queryFn: async () => {
            const response = await fetchWithAuth('/api/config/tasks');
            if (!response.ok) throw new Error('Failed to load tasks config');
            return response.json();
        },
        staleTime: Infinity, // Seldom changes, cache indefinitely
    });
    
    // Safely extract the tasks dictionary for this page's logic
    const tasksConfig = useMemo(() => tasksConfigData?.tasks || null, [tasksConfigData]);

    // Mutation for Full Name
    const { mutate: updateFullName, isPending: isNameLoading } = useMutation({
        mutationFn: async () => {
            const response = await fetchWithAuth('/api/user/change-fullname', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fullName: newFullName }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || t('accountPage.notifications.nameUpdateFailed'));
            return data;
        },
        onSuccess: (data) => {
            setNotification({ message: data.message, type: 'success' });
            setUser(prev => ({ ...prev, ...data.user }));
            setIsEditingName(false);
        },
        onError: (err) => setNotification({ message: err.message, type: 'error' })
    });

    // Mutation for Password
    const { mutate: updatePassword, isPending: isPasswordLoading } = useMutation({
        mutationFn: async () => {
            const response = await fetchWithAuth('/api/user/change-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ currentPassword, newPassword }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || t('accountPage.notifications.passwordUpdateFailed'));
            return data.message;
        },
        onSuccess: (message) => {
            setNotification({ message, type: 'success' });
            setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword('');
        },
        onError: (err) => setNotification({ message: err.message, type: 'error' })
    });

    // Mutation for Account Deletion
    const { mutate: deleteAccount, isPending: isDeleting } = useMutation({
        mutationFn: async () => {
            const response = await fetchWithAuth('/api/user/delete-account', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password: deletePassword }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || t('accountPage.notifications.deleteFailed'));
        },
        onSuccess: () => {
            alert(t('accountPage.notifications.deleteSuccess'));
            logout();
        },
        onError: (err) => setNotification({ message: err.message, type: 'error' }),
        onSettled: () => {
            setIsDeleteModalOpen(false);
            setDeletePassword('');
        }
    });

    // Apply tasksConfig once loaded without wiping active unsaved changes
    useEffect(() => {
        if (tasksConfig) {
            setAnalysisPrefs(getMergedPrefs(user?.analysis_preferences, tasksConfig));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tasksConfig]);

    // --- Threat Radar Scroll Logic ---
    const searchParams = new URLSearchParams(location.search);
    const shouldScrollToThreatRadar = !!location.state?.scrollToThreatRadar || searchParams.get('scrollTo') === 'threat-radar';
    const shouldOpenProactive = !!location.state?.openProactiveMonitoring || searchParams.get('openProactive') === 'true';

    // --- Accordion State Management ---
    const [expandedPanels, setExpandedPanels] = useState({
        panel1: false,
        panel2: false,
        panel3: false,
        panel4: false,
        panel5: shouldScrollToThreatRadar || shouldOpenProactive
    });
    const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

    // --- Inner Expand States for Threat Radar ---
    const [isMonitoringExpanded, setIsMonitoringExpanded] = useState(shouldOpenProactive);
    const [highlightProactiveSwitch, setHighlightProactiveSwitch] = useState(shouldOpenProactive);
    const [isEmailExpanded, setIsEmailExpanded] = useState(false);
    const proactiveSwitchRef = useRef(null);
    const [isPlatformsDropdownOpen, setIsPlatformsDropdownOpen] = useState(false);

    // Refs to keep Target Platforms dropdown & Priority Weight sliders open together
    const platformsSelectRef = useRef(null);
    const platformsMenuRef = useRef(null);
    const platformsWeightsRef = useRef(null);

    useEffect(() => {
        if (!isPlatformsDropdownOpen) return;

        const handleClickOutside = (event) => {
            const target = event.target;
            const clickedInsideSelect = platformsSelectRef.current?.contains(target);
            const clickedInsideMenu =
                platformsMenuRef.current?.contains(target) ||
                Boolean(target.closest?.('.MuiMenu-paper'));
            const clickedInsideWeights = platformsWeightsRef.current?.contains(target);

            if (!clickedInsideSelect && !clickedInsideMenu && !clickedInsideWeights) {
                setIsPlatformsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isPlatformsDropdownOpen]);

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedPanels(prev => ({ ...prev, [panel]: isExpanded }));
    };

    // Helper to render the inline save button (Disabled to favor the sticky bottom banner)
    const renderInlineSaveButton = (panelId) => {
        return null;
    };

    // --- Track if preferences have changed ---
    const originalAnalysisPrefs = useMemo(() => {
        return getMergedPrefs(user?.analysis_preferences, tasksConfig);
    }, [user?.analysis_preferences, tasksConfig]);

    const originalTrPrefs = useMemo(() => {
        return getMergedTrPrefs(user?.threat_radar_preferences);
    }, [user?.threat_radar_preferences]);

    const hasUnsavedAnalysisPrefs = useMemo(() => {
        if (!analysisPrefs || !originalAnalysisPrefs) return false;
        
        // Helper to sort arrays so ['a','b'] matches ['b','a']
        const sortArr = (arr) => [...(arr || [])].sort().join(',');
        
        const analysisChanged = (
            analysisPrefs.default_language !== originalAnalysisPrefs.default_language ||
            analysisPrefs.default_qr_label !== originalAnalysisPrefs.default_qr_label ||
            analysisPrefs.default_content_type !== originalAnalysisPrefs.default_content_type ||
            analysisPrefs.default_regions !== originalAnalysisPrefs.default_regions ||
            analysisPrefs.display_regions !== originalAnalysisPrefs.display_regions ||
            analysisPrefs.default_explanation !== originalAnalysisPrefs.default_explanation ||
            analysisPrefs.display_explanation !== originalAnalysisPrefs.display_explanation ||
            sortArr(analysisPrefs.displayed_content_types) !== sortArr(originalAnalysisPrefs.displayed_content_types) ||
            sortArr(analysisPrefs.default_tasks) !== sortArr(originalAnalysisPrefs.default_tasks) ||
            sortArr(analysisPrefs.displayed_tasks) !== sortArr(originalAnalysisPrefs.displayed_tasks) ||
            sortArr(analysisPrefs.enabled_meta_tasks) !== sortArr(originalAnalysisPrefs.enabled_meta_tasks)
        );

        const currencyChanged = currency !== (user?.preferred_currency || 'USD');
        const adToolsChanged = isAdToolsEnabled !== !!user?.show_ad_tools_by_default;
        
        // Deep equal helper to avoid JSON.stringify key-order issues when comparing dictionary defaults
        const isDeepEqual = (a, b) => {
            if (a === b) return true;
            if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false;
            
            // Order-agnostic array comparison
            if (Array.isArray(a) && Array.isArray(b)) {
                if (a.length !== b.length) return false;
                const sortedA = [...a].sort();
                const sortedB = [...b].sort();
                for (let i = 0; i < sortedA.length; i++) {
                    if (!isDeepEqual(sortedA[i], sortedB[i])) return false;
                }
                return true;
            }

            let keysA = Object.keys(a), keysB = Object.keys(b);
            if (keysA.length !== keysB.length) return false;
            for (let key of keysA) {
                if (!keysB.includes(key) || !isDeepEqual(a[key], b[key])) return false;
            }
            return true;
        };
        
        const trChanged = !isDeepEqual(trPrefs, originalTrPrefs);

        const hasPendingTrKeyword = trKeywordInput.trim() !== '';

        return analysisChanged || currencyChanged || adToolsChanged || trChanged || hasPendingTrKeyword;
    }, [analysisPrefs, originalAnalysisPrefs, currency, isAdToolsEnabled, user?.preferred_currency, user?.show_ad_tools_by_default, trPrefs, originalTrPrefs, trKeywordInput]);

    // 1. Sync the global unsaved changes state to block navigation from the Header component
    useEffect(() => {
        setIsDirty(hasUnsavedAnalysisPrefs);
        return () => setIsDirty(false);
    }, [hasUnsavedAnalysisPrefs, setIsDirty]);

    // 2. Prompt the user if they try to close the browser tab or refresh (Hard exit)
    useUnsavedChangesWarning(hasUnsavedAnalysisPrefs);

    // 3. Blur the page behind the modal when it opens
    useEffect(() => {
        setIsPageBlurred(isManualUnsavedDialogOpen || isRadarPasswordModalOpen);
        return () => setIsPageBlurred(false);
    }, [isManualUnsavedDialogOpen, isRadarPasswordModalOpen, setIsPageBlurred]);

    // 4. SPA Native Browser Back Button Interceptor (popstate trap)
    useEffect(() => {
        const handlePopState = (e) => {
            if (hasUnsavedAnalysisPrefs) {
                // Instantly push a dummy state back to prevent navigation
                window.history.pushState(null, '', window.location.href);
                setIsManualUnsavedDialogOpen(true);
            }
        };

        if (hasUnsavedAnalysisPrefs && !isTrapActive.current) {
            // Push dummy state to the history stack so we have something to intercept
            window.history.pushState(null, '', window.location.href);
            isTrapActive.current = true;
            window.addEventListener('popstate', handlePopState);
        } else if (!hasUnsavedAnalysisPrefs && isTrapActive.current) {
            // Unsaved changes were resolved (Saved), remove the trap seamlessly
            window.removeEventListener('popstate', handlePopState);
            window.history.back(); // Silently pops the dummy state
            isTrapActive.current = false;
        }

        return () => window.removeEventListener('popstate', handlePopState);
    }, [hasUnsavedAnalysisPrefs]);

    const scrollToSaveButton = () => {
        setTimeout(() => {
            if (saveButtonRef.current) {
                // Add a small shake animation instead of scrolling since the banner is fixed
                saveButtonRef.current.classList.add('shake-animation');
                setTimeout(() => {
                    if (saveButtonRef.current) {
                        saveButtonRef.current.classList.remove('shake-animation');
                    }
                }, 500);
            }
        }, 150); 
    };

    // Listen for the custom event from Header.jsx
    useEffect(() => {
        window.addEventListener('scrollToUnsavedChanges', scrollToSaveButton);
        return () => window.removeEventListener('scrollToUnsavedChanges', scrollToSaveButton);
    }, []);

    const handleStayOnPage = () => {
        setIsManualUnsavedDialogOpen(false);
        scrollToSaveButton();
    };

    const handleManualLeavePage = () => {
        setIsManualUnsavedDialogOpen(false);
        setIsDirty(false); // Clear the block
        navigate(-2); // Go back 2 steps: 1 to bypass our dummy state, 1 to actually leave the page
    };

    // Safely handle the smooth scroll when component mounts
    useEffect(() => {
        let timer;
        if (shouldOpenProactive && proactiveSwitchRef.current && !hasScrolledRef.current) {
            // Smoothly scroll directly to the proactive monitoring switch
            timer = setTimeout(() => {
                if (proactiveSwitchRef.current) {
                    proactiveSwitchRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    hasScrolledRef.current = true;
                }
            }, 350);
        } else if ((shouldScrollToThreatRadar || location.state?.scrollToPrefs) && prefsSectionRef.current && !hasScrolledRef.current) {
            timer = setTimeout(() => {
                if (prefsSectionRef.current) {
                    prefsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    hasScrolledRef.current = true;
                }
            }, 300);
        }
        
        return () => clearTimeout(timer);
    }, [shouldScrollToThreatRadar, shouldOpenProactive, location.state]);

    // Auto-dismiss the glow highlight after 8 seconds or if the user clicks outside
    useEffect(() => {
        if (highlightProactiveSwitch) {
            // 1. Setup the auto-dismiss timer
            const timer = setTimeout(() => setHighlightProactiveSwitch(false), 8000);
            
            // 2. Setup the click-outside listener
            const handleClickOutside = (event) => {
                if (proactiveSwitchRef.current && !proactiveSwitchRef.current.contains(event.target)) {
                    setHighlightProactiveSwitch(false);
                }
            };
            
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);

            // 3. Cleanup both the timer and listeners when effect unmounts or state changes
            return () => {
                clearTimeout(timer);
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('touchstart', handleClickOutside);
            };
        }
    }, [highlightProactiveSwitch]);

    useEffect(() => {
        if (notification.message) {
            const timerId = setTimeout(() => setNotification({ message: '', type: '' }), 5000);
            return () => clearTimeout(timerId);
        }
    }, [notification]);

    // --- Update Analysis Prefs Handlers ---
    const handleAnalysisPrefChange = (key, value) => {
        setAnalysisPrefs(prev => {
            const next = { ...prev, [key]: value };

            if (key === 'displayed_content_types') {
                if (value.length === 0) return prev;
                if (!value.includes(next.default_content_type)) {
                    const nextDefault = ORDERED_CONTENT_TYPES.find(ct => value.includes(ct));
                    next.default_content_type = nextDefault || value[0];
                }
            }

            if (key === 'displayed_tasks') {
                if (value.length === 0) return prev;

                // Determine which tasks were just added
                const prevDisplayed = prev.displayed_tasks || [];
                const addedTasks = value.filter(t => !prevDisplayed.includes(t));

                // Start from previous default_tasks (or empty)
                const prevDefault = Array.isArray(next.default_tasks) ? [...next.default_tasks] : [];

                // If tasks were added, add them to default_tasks so "Enabled by Default" turns on
                if (addedTasks.length > 0) {
                    addedTasks.forEach(t => {
                        if (!prevDefault.includes(t)) prevDefault.push(t);
                    });
                }

                // Ensure default_tasks only contains tasks that are still displayed
                next.default_tasks = prevDefault.filter(t => value.includes(t));
            }

            // Cascading disable for Meta-Scores if their dependencies are turned off
            if (key === 'displayed_tasks' || key === 'default_tasks') {
                const currentDisplayed = key === 'displayed_tasks' ? value : next.displayed_tasks;
                const currentDefault = key === 'default_tasks' ? value : next.default_tasks;
                
                if (next.enabled_meta_tasks && next.enabled_meta_tasks.length > 0) {
                    next.enabled_meta_tasks = next.enabled_meta_tasks.filter(metaKey => {
                        const deps = tasksConfig[metaKey]?.depends_on || [];
                        // Keep meta task ONLY if ALL its dependencies are both displayed and enabled by default
                        return deps.every(d => currentDisplayed.includes(d) && currentDefault.includes(d));
                    });
                }
            }

            if (key === 'display_regions' && !value) {
                next.default_regions = false;
            }
            if (key === 'display_explanation' && !value) {
                next.default_explanation = false;
            }

            return next;
        });
    };

    const { mutate: saveAllPreferences, isPending: isSavingAnalysisPrefs } = useMutation({
        mutationFn: async () => {
            const promises = [];
            const sortArr = (arr) => [...(arr || [])].sort().join(',');
            const analysisChanged = (
                analysisPrefs.default_language !== originalAnalysisPrefs.default_language ||
                analysisPrefs.default_qr_label !== originalAnalysisPrefs.default_qr_label ||
                analysisPrefs.default_content_type !== originalAnalysisPrefs.default_content_type ||
                analysisPrefs.default_regions !== originalAnalysisPrefs.default_regions ||
                analysisPrefs.display_regions !== originalAnalysisPrefs.display_regions ||
                analysisPrefs.default_explanation !== originalAnalysisPrefs.default_explanation ||
                analysisPrefs.display_explanation !== originalAnalysisPrefs.display_explanation ||
                sortArr(analysisPrefs.displayed_content_types) !== sortArr(originalAnalysisPrefs.displayed_content_types) ||
                sortArr(analysisPrefs.default_tasks) !== sortArr(originalAnalysisPrefs.default_tasks) ||
                sortArr(analysisPrefs.displayed_tasks) !== sortArr(originalAnalysisPrefs.displayed_tasks) ||
                sortArr(analysisPrefs.enabled_meta_tasks) !== sortArr(originalAnalysisPrefs.enabled_meta_tasks)
            );

            if (analysisChanged) promises.push(updateUserSetting('analysis_preferences', analysisPrefs));
            if (currency !== (user?.preferred_currency || 'USD')) promises.push(updateUserSetting('preferred_currency', currency));
            if (isAdToolsEnabled !== !!user?.show_ad_tools_by_default) promises.push(updateUserSetting('show_ad_tools_by_default', isAdToolsEnabled));

            let currentTrPrefs = { ...trPrefs };
            if (trKeywordInput.trim() !== '') {
                const val = trKeywordInput.trim();
                if (!currentTrPrefs.default_keywords.some(k => k.toLowerCase() === val.toLowerCase())) {
                    const newArr = [...currentTrPrefs.default_keywords, val];
                    const lowercasedUnique = [...new Set(newArr.map(k => k.trim().toLowerCase()))].filter(k => k !== '');
                    const limited = lowercasedUnique.slice(0, MAX_TR_KEYWORDS);
                    currentTrPrefs.default_keywords = limited;
                    setTrPrefs(prev => ({ ...prev, default_keywords: limited }));
                }
                setTrKeywordInput('');
            }

            // AUTO-DESELECT: Remove platforms where the weight was set to exactly 0
            if (currentTrPrefs.monitoring_platforms && currentTrPrefs.monitoring_weights) {
                currentTrPrefs.monitoring_platforms = currentTrPrefs.monitoring_platforms.filter(
                    plat => (currentTrPrefs.monitoring_weights[plat] ?? 0.5) > 0
                );
            }

            // Ensure every active platform has an explicit sort method seeded
            const seededSortMethods = { ...(currentTrPrefs.monitoring_sort_methods || {}) };
            (currentTrPrefs.monitoring_platforms || []).forEach(p => {
                if (!seededSortMethods[p]) {
                    seededSortMethods[p] = 'popular';
                }
            });
            currentTrPrefs.monitoring_sort_methods = seededSortMethods;
            setTrPrefs(prev => ({ 
                ...prev, 
                monitoring_platforms: currentTrPrefs.monitoring_platforms,
                monitoring_sort_methods: seededSortMethods 
            }));

            const trChanged = JSON.stringify(currentTrPrefs) !== JSON.stringify({ ...TR_SYSTEM_DEFAULTS, ...(user?.threat_radar_preferences || {}) });
            if (trChanged) promises.push(updateUserSetting('threat_radar_preferences', currentTrPrefs));

            if (promises.length > 0) {
                const results = await Promise.all(promises);
                const failedResult = results.find(r => !r.success);
                if (failedResult) throw new Error(failedResult.error || t('accountPage.preferences.saveFailure'));
            }
            return true;
        },
        onSuccess: () => {
            setShowSuccessOverlay(true);
            i18n.changeLanguage(analysisPrefs.default_language);
            setTimeout(() => setShowSuccessOverlay(false), 1500);
        },
        onError: (err) => setNotification({ message: err.message, type: 'error' })
    });

    const handleSaveAnalysisPrefs = () => {
        if (analysisPrefs.displayed_content_types.length === 0) {
            setNotification({ message: t('accountPage.analysisPrefs.errorNoContentType'), type: 'error' });
            return;
        }
        if (analysisPrefs.displayed_tasks.length === 0) {
            setNotification({ message: t('accountPage.analysisPrefs.errorNoTask'), type: 'error' });
            return;
        }
        setNotification({ message: '', type: '' });
        saveAllPreferences(); // Triggers the mutation
    };

    const handleDiscardChanges = () => {
        // Revert all tracked states back to their original/database values
        setAnalysisPrefs(originalAnalysisPrefs);
        setCurrency(user?.preferred_currency || 'USD');
        setIsAdToolsEnabled(!!user?.show_ad_tools_by_default);
        setTrPrefs(originalTrPrefs);
        setTrKeywordInput('');
        setIsDirty(false); // Clear the block
    };

    const handleFullNameChangeSubmit = (e) => {
        e.preventDefault();
        setNotification({ message: '', type: '' });
        if (!newFullName || !newFullName.trim()) {
            return setNotification({ message: t('accountPage.notifications.nameEmpty'), type: 'error' });
        }
        updateFullName();
    };

    const handlePasswordChangeSubmit = (e) => {
        e.preventDefault();
        setNotification({ message: '', type: '' });
        if (newPassword !== confirmNewPassword) {
            return setNotification({ message: t('accountPage.notifications.passwordsNoMatch'), type: 'error' });
        }
        if (passwordError) return setNotification({ message: passwordError, type: 'error' });
        updatePassword();
    };
    
    const handleDeleteAccountSubmit = () => {
        setNotification({ message: '', type: '' });
        deleteAccount();
    };

    const handleVerifyRadarPassword = async (e) => {
        e.preventDefault();
        setIsVerifyingRadarPassword(true);
        setRadarPasswordError('');
        try {
            const response = await fetchWithAuth('/api/threat-radar/verify-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password: radarPasswordInput }),
            });
            const data = await response.json();
            if (response.ok && data.valid) {
                setTrPrefs(prev => ({...prev, active_monitoring: true}));
                setIsMonitoringExpanded(true);
                setHighlightProactiveSwitch(false);
                setIsRadarPasswordModalOpen(false);
                setRadarPasswordInput('');
            } else {
                const errorKey = data.error 
                    ? `accountPage.threatRadarPrefs.passwordErrors.${data.error}`
                    : 'accountPage.threatRadarPrefs.passwordErrors.INCORRECT_PASSWORD';
                setRadarPasswordError(t(errorKey, { defaultValue: data.message || 'Incorrect password.' }));
            }
        } catch (err) {
            setRadarPasswordError(t('accountPage.threatRadarPrefs.passwordErrors.NETWORK_ERROR', { defaultValue: 'An error occurred checking the password.' }));
        } finally {
            setIsVerifyingRadarPassword(false);
        }
    };

    const validateNewPassword = (value) => {
        setNewPassword(value);
        if (value && !validator.isStrongPassword(value, { minLength: 8, minLowercase: 1, minUppercase: 1, minNumbers: 1, minSymbols: 1 })) {
            setPasswordError(t('accountPage.passwordSection.passwordStrengthError'));
        } else {
            setPasswordError('');
        }
    };

    const handleAdToolsToggle = (event) => {
        setIsAdToolsEnabled(event.target.checked);
    };

    const handleCurrencyChange = (event) => {
        setCurrency(event.target.value); 
    };

    const handleMouseDownPassword = (event) => event.preventDefault();
    // Helper for Translation Fallback
    const getTaskDisplayName = (taskKey, yamlFallback) => {
        const camelCaseKey = taskKey.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
        const paths = [
            `selector.analysisTasks.${taskKey}`, 
            `selector.analysisTasks.${camelCaseKey}`, 
            `analysisTasks.${taskKey}`, 
            `analysisTasks.${camelCaseKey}`,
            `verificationPage.taskDescriptions.${taskKey}`
        ];
        for (let path of paths) {
            const res = t(path);
            if (res !== path) return res;
        }
        return yamlFallback;
    };

    const cameFromThreatRadar = shouldScrollToThreatRadar;

    const DashboardScopeIcon = () => (
        <Tooltip title={t('accountPage.threatRadarPrefs.legend.dashboard', 'Affects Threat Radar Dashboard')} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
            <ManageSearchIcon sx={{ color: '#a9a9a9', fontSize: '1.5rem', ml: 0, mr: -0.9, opacity: 0.9, cursor: 'help' }} />
        </Tooltip>
    );

    const ProactiveScopeIcon = () => (
        <Tooltip title={t('accountPage.threatRadarPrefs.legend.proactive', 'Affects Proactive Background Monitoring')} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
            <TravelExploreIcon sx={{ color: '#F59E0B', fontSize: '1.1rem', ml: 0, mr: 0, opacity: 0.9, cursor: 'help' }} />
        </Tooltip>
    );

    return (
        <div className="account-page-wrapper">
            <Helmet>
                <title>{t('accountPage.helmet.title')}</title>
            </Helmet>
            <div 
                className="account-header-wrapper"
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1000,
                    alignSelf: 'flex-start', // Prevents flexbox from stretching it weirdly
                    width: '100%',
                    backgroundColor: 'var(--background-color-main-app)' // Prevents text from showing through the header when scrolling
                }}
            >
                <Header 
                    showDataFlowPreview={false} 
                    showBackButton={true} 
                    backButtonLink={cameFromThreatRadar ? "/app/threat-radar" : "/app"}
                    backButtonLabel={cameFromThreatRadar ? t('header.backToThreats') : t('header.backToAnalyzer')}
                />
            </div>
            <main className="account-main-content">
                <h1>{t('accountPage.mainTitle')}</h1>
                <p className="account-subtitle">{t('accountPage.subtitle')}</p>

                {/* --- 1. Account Details --- */}
                <Box className="account-section">
                    <div className="account-section-header">
                        <h2>{t('accountPage.detailsSection.title')}</h2>
                        <Button
                            variant="outlined"
                            className="edit-button"
                            onClick={() => {
                                setNewFullName(user?.full_name || '');
                                setIsEditingName(prev => !prev);
                            }}
                        >
                            {isEditingName ? t('accountPage.detailsSection.cancel') : t('accountPage.detailsSection.edit')}
                        </Button>
                    </div>

                    {isEditingName ? (
                        <Box component="form" onSubmit={handleFullNameChangeSubmit} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <TextField
                                label={t('accountPage.detailsSection.fullNameLabel')}
                                value={newFullName}
                                onChange={(e) => setNewFullName(e.target.value)}
                                fullWidth autoFocus variant="outlined" size="small"
                            />
                            <Button type="submit" variant="contained" disabled={isNameLoading} className="action-button">
                                {isNameLoading ? <CircularProgress size={24} /> : t('accountPage.detailsSection.save')}
                            </Button>
                        </Box>
                    ) : (
                        <Box className="details-grid">
                            <span>{t('accountPage.detailsSection.fullNameLabel')}:</span><strong>{user?.full_name || 'Not Provided'}</strong>
                            <span>{t('accountPage.detailsSection.emailLabel')}:</span><strong>{user?.email}</strong>
                        </Box>
                    )}
                </Box>

                {/* --- 2. Change Password --- */}
                {(!user?.auth_provider || user?.auth_provider === 'email') && (
                    <Box className="account-section">
                        <h2 className="account-section-title">{t('accountPage.passwordSection.title')}</h2>
                        <Box component="form" onSubmit={handlePasswordChangeSubmit} className="password-form">
                            <TextField variant="outlined" label={t('accountPage.passwordSection.currentPasswordLabel')} type={showCurrentPassword ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required fullWidth InputProps={{ endAdornment: ( <InputAdornment position="end"> <IconButton aria-label="toggle current password visibility" onClick={() => setShowCurrentPassword(!showCurrentPassword)} onMouseDown={handleMouseDownPassword} edge="end"> {showCurrentPassword ? <VisibilityOff /> : <Visibility />} </IconButton> </InputAdornment> )}} />
                            <TextField variant="outlined" label={t('accountPage.passwordSection.newPasswordLabel')} type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => validateNewPassword(e.target.value)} required fullWidth error={!!passwordError} helperText={passwordError} InputProps={{ endAdornment: ( <InputAdornment position="end"> <IconButton aria-label="toggle new password visibility" onClick={() => setShowNewPassword(!showNewPassword)} onMouseDown={handleMouseDownPassword} edge="end"> {showNewPassword ? <VisibilityOff /> : <Visibility />} </IconButton> </InputAdornment> )}} />
                            <TextField variant="outlined" label={t('accountPage.passwordSection.confirmNewPasswordLabel')} type={showNewPassword ? 'text' : 'password'} value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} required fullWidth InputProps={{ endAdornment: ( <InputAdornment position="end"> <IconButton aria-label="toggle confirm password visibility" onClick={() => setShowConfirmPassword(!showConfirmPassword)} onMouseDown={handleMouseDownPassword} edge="end"> {showConfirmPassword ? <VisibilityOff /> : <Visibility />} </IconButton> </InputAdornment> )}} />
                            <Button type="submit" variant="contained" disabled={isPasswordLoading} className="action-button">
                                {isPasswordLoading ? <CircularProgress size={24} color="inherit" /> : t('accountPage.passwordSection.updateButton')}
                            </Button>
                        </Box>
                    </Box>
                )}
            
                {/* --- 3. API Keys Management --- */} 
                <Box className="account-section">
                    <ApiKeysManager />
                </Box>

                {/* --- 4. Workspace & Initialization Preferences --- */}
                <Box className="account-section" ref={prefsSectionRef} sx={{ scrollMarginTop: '100px' }}>
                    <h2 className="account-section-title">{t('accountPage.analysisPrefs.title')}</h2>
                    <Typography variant="body2" className="setting-description" sx={{ pl: 0, mb: 3 }}>
                        {t('accountPage.analysisPrefs.description')}
                    </Typography>

                    <div className="prefs-accordion-container">

                        {/* Accordion: Threat Radar Preferences */}
                        <Accordion 
                            className="pref-accordion" 
                            expanded={expandedPanels.panel5} 
                            onChange={handleAccordionChange('panel5')}
                            disableGutters 
                            ref={threatRadarRef}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <NotificationsActiveIcon sx={{ color: 'var(--secondary-color)', fontSize: '1.3rem' }} />
                                        <Typography className="pref-group-title" sx={{ mb: 0, borderBottom: 'none', pb: 0 }}>
                                            {t('accountPage.threatRadarPrefs.title', 'Threat Radar Preferences')}
                                        </Typography>
                                        {renderInlineSaveButton('panel5')}
                                    </Box>
                                    
                                    {expandedPanels.panel5 && (
                                        <Button 
                                            size="small"
                                            startIcon={<HelpOutlineIcon sx={{ fontSize: '1.1rem !important' }} />}
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevents accordion from toggling when clicking the docs button
                                                openDocViewer('user_guides/threat_radar.md');
                                            }}
                                            sx={{ 
                                                color: 'var(--secondary-color)', textTransform: 'none', fontSize: '0.8rem',
                                                fontWeight: 600, letterSpacing: '0.3px', py: 0.5, px: 1, borderRadius: '6px',
                                                whiteSpace: 'nowrap',
                                                transition: 'all 0.2s ease', '&:hover': { background: 'rgba(79, 195, 247, 0.1)', color: '#fff' }
                                            }}
                                        >
                                            {t('accountPage.threatRadarPrefs.readDocsTooltip')}
                                        </Button>
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', m: 0 }}>
                                        {t('accountPage.threatRadarPrefs.description', 'Configure your default tracking keywords and filters for the Threat Radar monitor.')}
                                    </Typography>
                                </Box>

                                {/* Legend */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3, p: 1.5 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, letterSpacing: '0.3px' }}>
                                        {t('accountPage.threatRadarPrefs.legend.title', 'Legend:')}
                                    </Typography>
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px' }}>
                                            <ManageSearchIcon sx={{ color: '#a9a9a9', fontSize: '1.4rem' }} />
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1 }}>
                                            {t('accountPage.threatRadarPrefs.legend.dashboard', 'Affects Threat Radar Dashboard')}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px' }}>
                                            <TravelExploreIcon sx={{ color: '#F59E0B', fontSize: '1.15rem' }} />
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1 }}>
                                            {t('accountPage.threatRadarPrefs.legend.proactive', 'Affects Proactive Background Monitoring')}
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    
                                    {/* Keywords */}
                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', m: 0, display: 'inline-flex', alignItems: 'center', fontWeight: 500 }}>
                                                {t('accountPage.threatRadarPrefs.trackedKeywords', { count: trPrefs.default_keywords.length, max: MAX_TR_KEYWORDS })}
                                                <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.trackedKeywords', 'Keywords to auto-populate the dashboard search and for the background scraper to monitor.')} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                    <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', ml: 1, mr: 1, cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                                </Tooltip>
                                                <DashboardScopeIcon />
                                                <Tooltip title={t('accountPage.threatRadarPrefs.legend.proactive', 'Affects Proactive Background Monitoring')} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                    <TravelExploreIcon sx={{ color: '#F59E0B', fontSize: '1.1rem', ml: 0.9, display: 'inline-flex', opacity: 0.9, cursor: 'help' }} />
                                                </Tooltip>
                                            </Typography>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {/* CLEAR ALL BUTTON */}
                                                <Tooltip title={t('accountPage.threatRadarPrefs.keywordAssistant.clearAllKeywords', 'Clear all keywords')} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                    <span>
                                                        <IconButton 
                                                            size="small" 
                                                            disabled={trPrefs.default_keywords.length === 0}
                                                            onClick={() => {
                                                                setTrPrefs(prev => {
                                                                    const next = { ...prev, default_keywords: [] };
                                                                    next.active_monitoring = false;
                                                                    next.email_alerts_enabled = false;
                                                                    next.automated_takedowns = false;
                                                                    return next;
                                                                });
                                                                setIsMonitoringExpanded(false);
                                                                setIsEmailExpanded(false);
                                                                setTrKeywordInput('');
                                                            }}
                                                            sx={{ 
                                                                color: 'rgba(255,255,255,0.4)', 
                                                                '&:hover': { color: '#F44336', backgroundColor: 'rgba(244, 67, 54, 0.1)' },
                                                                '&.Mui-disabled': { opacity: 0.3 }
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            
                                                {/* THE AI SPARKLE BUTTON */}
                                                <Tooltip 
                                                    title={trPrefs.default_keywords.length >= MAX_TR_KEYWORDS ? t('accountPage.threatRadarPrefs.tooltips.maxKeywordsReached', { max: MAX_TR_KEYWORDS, defaultValue: 'Maximum of {{max}} keywords allowed' }) : ""}
                                                    arrow 
                                                    placement="top" 
                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                >
                                                    <span style={{ display: 'inline-flex' }}>
                                                        <Button
                                                            size="small"
                                                            startIcon={<AutoAwesomeIcon sx={{ fontSize: '0.95rem !important', color: 'inherit' }} />}
                                                            onClick={() => setIsKeywordModalOpen(true)}
                                                            disabled={trPrefs.default_keywords.length >= MAX_TR_KEYWORDS}
                                                            sx={{
                                                                backgroundColor: 'var(--secondary-color)',
                                                                color: 'white',
                                                                border: 'none',
                                                                textTransform: 'none',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 'bold',
                                                                borderRadius: '6px',
                                                                px: 1.5,
                                                                py: 0.4,
                                                                boxShadow: 'none',
                                                                transition: 'background-color 0.3s ease, transform 0.1s ease',
                                                                '&:hover': {
                                                                    backgroundColor: 'var(--btn-hover-color)',
                                                                    boxShadow: 'none'
                                                                },
                                                                '&:active': {
                                                                    transform: 'scale(0.98)'
                                                                },
                                                                '&.Mui-disabled': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.12) !important',
                                                                    color: 'rgba(255, 255, 255, 0.2) !important',
                                                                    border: 'none !important'
                                                                }
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                {t('accountPage.threatRadarPrefs.keywordAssistant.suggestedKeywords', 'Suggested Keywords')}
                                                                <Tooltip 
                                                                    title={t('accountPage.threatRadarPrefs.keywordAssistant.infoTooltip')} 
                                                                    arrow 
                                                                    placement="top" 
                                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                                >
                                                                    <InfoOutlinedIcon 
                                                                        onClick={(e) => e.stopPropagation()} 
                                                                        sx={{ fontSize: '0.95rem', ml: 0.75, opacity: 0.8, '&:hover': { opacity: 1 } }} 
                                                                    />
                                                                </Tooltip>
                                                            </Box>
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        {(() => {
                                            const isDuplicateTr = trKeywordInput.trim() !== '' && trPrefs.default_keywords.some(kw => kw.toLowerCase() === trKeywordInput.trim().toLowerCase());
                                            const isMaxTr = trPrefs.default_keywords.length >= MAX_TR_KEYWORDS;
                                            const hasNonEnglishCharsTr = trKeywordInput.trim() !== '' && !/^[\x20-\x7E]*$/.test(trKeywordInput);
                                            const wordCountTr = trKeywordInput.trim() ? trKeywordInput.trim().split(/\s+/).length : 0;
                                            const isTooManyWordsTr = wordCountTr > 3;
                                            
                                            const showTipTr = (isMaxTr && trKeywordInput.length > 0) || isDuplicateTr || hasNonEnglishCharsTr || isTooManyWordsTr;
                                            
                                            let tooltipMsgTr = "";
                                            if (isMaxTr && trKeywordInput.length > 0) tooltipMsgTr = t('maxKeywords', { max: MAX_TR_KEYWORDS, defaultValue: `Maximum of ${MAX_TR_KEYWORDS} keywords allowed` });
                                            else if (isDuplicateTr) tooltipMsgTr = t('accountPage.threatRadarPrefs.keywordAssistant.alreadyAdded', 'Already added');
                                            else if (isTooManyWordsTr) tooltipMsgTr = t('ThreatRadarPage.filters.maxWords', { defaultValue: 'Max 3 words allowed per keyword' });
                                            else if (hasNonEnglishCharsTr) tooltipMsgTr = t('ThreatRadarPage.filters.englishWarning');

                                            return (
                                                <Tooltip title={tooltipMsgTr} open={showTipTr} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                    <Box sx={{
                                                        borderWidth: '1px', // Keep border static to prevent jumping
                                                        borderStyle: 'solid',
                                                        borderColor: isHoveringDisabledSwitch ? 'var(--secondary-color)' : 'rgba(255,255,255,0.3)',
                                                        borderRadius: '4px',
                                                        maxHeight: '120px',
                                                        overflowY: 'auto',
                                                        overflowX: 'hidden',
                                                        backgroundColor: isHoveringDisabledSwitch ? 'rgba(79, 195, 247, 0.08)' : 'transparent',
                                                        transition: 'all 0.3s ease',
                                                        // Use box-shadow to create the 2px thickness effect inwards without altering layout size
                                                        boxShadow: isHoveringDisabledSwitch ? 'inset 0 0 0 1px var(--secondary-color), 0 0 12px rgba(79, 195, 247, 0.5)' : 'none',
                                                        '&:hover': { borderColor: isHoveringDisabledSwitch ? 'var(--secondary-color)' : 'rgba(255,255,255,0.5)' },
                                                        '&:focus-within': { borderColor: 'var(--secondary-color)', boxShadow: 'inset 0 0 0 1px var(--secondary-color)' }, 
                                                        '&::-webkit-scrollbar': { width: '6px' },
                                                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                                                        '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.2)', borderRadius: '10px' },
                                                        '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255, 255, 255, 0.4)' }
                                                    }}>
                                                        <Autocomplete
                                                            multiple
                                                            freeSolo
                                                            disableClearable
                                                            options={[]}
                                                            value={trPrefs.default_keywords}
                                                            sx={{ width: '100%' }}
                                                            onChange={(e, val) => {
                                                                const uniqueMap = new Map();
                                                                val.forEach(k => {
                                                                    const trimmed = k.trim();
                                                                    if (trimmed && !uniqueMap.has(trimmed.toLowerCase())) {
                                                                        uniqueMap.set(trimmed.toLowerCase(), trimmed);
                                                                    }
                                                                });
                                                                const limited = Array.from(uniqueMap.values()).slice(0, MAX_TR_KEYWORDS);
                                                                
                                                                const addedKeywords = limited.filter(kw => !trPrefs.default_keywords.includes(kw));
                                                                
                                                                setTrPrefs(prev => {
                                                                    const next = { ...prev, default_keywords: limited };
                                                                    if (limited.length === 0) {
                                                                        next.active_monitoring = false;
                                                                        next.email_alerts_enabled = false;
                                                                        next.automated_takedowns = false;
                                                                    }
                                                                    return next;
                                                                });

                                                                if (limited.length === 0) {
                                                                    setIsMonitoringExpanded(false);
                                                                    setIsEmailExpanded(false);
                                                                }
                                                                
                                                                if (addedKeywords.length > 0) {
                                                                    verifyTrKeywordLanguage(addedKeywords);
                                                                }
                                                            }}
                                                            inputValue={trKeywordInput}
                                                            onInputChange={(event, newInputValue, reason) => {
                                                                if (reason === 'input' || reason === 'clear') setTrKeywordInput(newInputValue);
                                                                else if (reason === 'reset') setTrKeywordInput(''); 
                                                            }}
                                                            renderTags={(value, getTagProps) =>
                                                                value.map((option, index) => {
                                                                    const failsFrontend = !/^[\x20-\x7E]*$/.test(option);
                                                                    const status = trKeywordLangStatus[option];
                                                                    const isChecking = status === 'checking';
                                                                    const failsBackend = status && status !== 'en' && status !== 'checking';
                                                                    const isNonEnglish = failsFrontend || failsBackend;

                                                                    let detectedLangName = status;
                                                                    if (failsBackend) {
                                                                        try {
                                                                            const displayNames = new Intl.DisplayNames([i18n.language || 'en'], { type: 'language' });
                                                                            detectedLangName = displayNames.of(status);
                                                                        } catch (e) {
                                                                            detectedLangName = status.toUpperCase();
                                                                        }
                                                                    }

                                                                    const chipTooltip = isNonEnglish ? (
                                                                        <div style={{ padding: '2px' }}>
                                                                            {t('ThreatRadarPage.filters.englishWarning')}
                                                                            {failsBackend && (
                                                                                <span style={{ display: 'block', marginTop: '8px', color: '#FFB142', fontWeight: 'bold', textTransform: 'capitalize' }}>
                                                                                    {t('ThreatRadarPage.filters.detectedLanguage')}: {detectedLangName}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ) : option.length > 25 ? option : "";

                                                                    return (
                                                                        <Tooltip 
                                                                            key={index}
                                                                            title={chipTooltip} 
                                                                            arrow 
                                                                            disableHoverListener={!isNonEnglish && option.length <= 25}
                                                                            PopperProps={{ className: 'global-dark-tooltip' }}
                                                                        >
                                                                            <Chip 
                                                                                variant="outlined" 
                                                                                {...getTagProps({ index })} 
                                                                                size="small" 
                                                                                label={
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                        <span style={{ 
                                                                                            whiteSpace: 'nowrap', 
                                                                                            overflow: 'hidden', 
                                                                                            textOverflow: 'ellipsis',
                                                                                            maxWidth: '220px',
                                                                                            display: 'block' 
                                                                                        }}>
                                                                                            {option}
                                                                                        </span>
                                                                                        {isChecking && <CircularProgress size={10} sx={{ color: 'inherit' }} />}
                                                                                    </Box>
                                                                                }
                                                                                sx={{ 
                                                                                    backgroundColor: isNonEnglish ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255,255,255,0.05)', 
                                                                                    borderColor: isNonEnglish ? '#FF9800' : 'rgba(255,255,255,0.3)',
                                                                                    color: isNonEnglish ? '#FF9800' : 'white',
                                                                                    fontWeight: 400,
                                                                                    maxWidth: '100%',
                                                                                    opacity: isChecking ? 0.6 : 1,
                                                                                    transition: 'all 0.2s ease',
                                                                                    '& .MuiChip-deleteIcon': {
                                                                                        color: isNonEnglish ? '#FF9800' : 'white',
                                                                                        opacity: 0.7,
                                                                                        fontSize: '18px',
                                                                                        '&:hover': {
                                                                                            opacity: 1,
                                                                                            color: isNonEnglish ? '#FFB142' : 'white'
                                                                                        }
                                                                                    }
                                                                                }} 
                                                                            />
                                                                        </Tooltip>
                                                                    );
                                                                })
                                                        }
                                                            renderInput={(params) => (
                                                                <TextField 
                                                                    {...params} 
                                                                    variant="outlined" 
                                                                    size="small" 
                                                                    placeholder={trPrefs.default_keywords.length === 0 ? t('ThreatRadarPage.filters.typePlaceholder') : ""} 
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' && (isDuplicateTr || isTooManyWordsTr)) {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                        }
                                                                    }}
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    style: {
                                                                        color: (isDuplicateTr || isTooManyWordsTr) ? '#F44336' : (hasNonEnglishCharsTr ? '#FF9800' : 'white'),
                                                                        fontWeight: 600
                                                                    },
                                                                    onBlur: (e) => {
                                                                        if (params.inputProps.onBlur) {
                                                                            params.inputProps.onBlur(e);
                                                                        }
                                                                        handleCommitPendingTrKeyword();
                                                                    }
                                                                }}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': { 
                                                                        alignItems: 'flex-start',
                                                                        padding: '6px',
                                                                        '& fieldset': { display: 'none' }
                                                                    },
                                                                    '& .MuiAutocomplete-input': { 
                                                                        transition: 'color 0.2s ease',
                                                                        padding: '2px 4px !important'
                                                                    }
                                                                }}
                                                                />
                                                            )}
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            );
                                        })()}
                                    </Box>

                                    {/* --- Active Monitoring Master Switch --- */}
                                    <Box 
                                        ref={proactiveSwitchRef}
                                        sx={{ 
                                            bgcolor: highlightProactiveSwitch ? 'rgba(245, 158, 11, 0.08)' : 'rgba(255,255,255,0.02)', 
                                            p: 0, 
                                            borderRadius: 1, 
                                            border: '1px solid',
                                            borderColor: highlightProactiveSwitch ? '#F59E0B' : 'rgba(255,255,255,0.05)',
                                            boxShadow: highlightProactiveSwitch ? '0 0 18px rgba(245, 158, 11, 0.45)' : 'none',
                                            overflow: 'visible',
                                            transition: 'all 0.4s ease'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: 500 }}>
                                                    {t('accountPage.threatRadarPrefs.activeMonitoringLabel', 'Enable Proactive Background Monitoring')}
                                                </Typography>
                                                <Tooltip title={t('accountPage.threatRadarPrefs.activeMonitoringTooltip', 'When enabled, Vetra AI will autonomously search the web for your keywords at regular intervals to find new threats.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                    <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                                </Tooltip>
                                                <ProactiveScopeIcon />
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {(() => {
                                                    const isProactiveHighlighted = !hasNoKeywords && highlightProactiveSwitch;

                                                    return (
                                                        <Tooltip 
                                                            key={isProactiveHighlighted ? 'highlighted' : 'normal'}
                                                            title={
                                                                hasNoKeywords 
                                                                    ? t('accountPage.threatRadarPrefs.tooltips.requiresKeywords', 'Add at least 1 keyword to enable this feature.') 
                                                                    : isProactiveHighlighted 
                                                                        ? t('accountPage.threatRadarPrefs.tooltips.enableProactiveActionTooltip', 'Switch ON to enable Proactive Background Monitoring') 
                                                                        : ''
                                                            } 
                                                            {...(isProactiveHighlighted ? { open: true } : {})}
                                                            arrow 
                                                            placement="top"
                                                            PopperProps={{ className: 'global-dark-tooltip' }}
                                                        >
                                                            <span 
                                                                onMouseEnter={() => hasNoKeywords && setIsHoveringDisabledSwitch(true)}
                                                                onMouseLeave={() => setIsHoveringDisabledSwitch(false)}
                                                                style={{ cursor: hasNoKeywords ? 'not-allowed' : 'pointer', display: 'inline-flex' }}
                                                            >
                                                                <Switch
                                                                    className="custom-explanation-switch"
                                                                    size="small"
                                                                    checked={trPrefs.active_monitoring || false}
                                                                    disabled={hasNoKeywords}
                                                                    sx={{ pointerEvents: hasNoKeywords ? 'none' : 'auto' }}
                                                                    onChange={(e) => {
                                                                        const isChecked = e.target.checked;
                                                                        if (isChecked && radarConfigData?.require_password) {
                                                                            setIsRadarPasswordModalOpen(true);
                                                                        } else {
                                                                            setTrPrefs(prev => ({...prev, active_monitoring: isChecked}));
                                                                            setIsMonitoringExpanded(isChecked);
                                                                            setHighlightProactiveSwitch(false);
                                                                        }
                                                                    }}
                                                                />
                                                            </span>
                                                        </Tooltip>
                                                    );
                                                })()}
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => setIsMonitoringExpanded(!isMonitoringExpanded)}
                                                    sx={{ 
                                                        color: 'rgba(255,255,255,0.6)', 
                                                        transform: isMonitoringExpanded ? 'rotate(180deg)' : 'rotate(0deg)', 
                                                        transition: 'transform 0.3s ease',
                                                        padding: '2px'
                                                    }}
                                                >
                                                    <ExpandMoreIcon />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        {/* Background Monitoring Sub-Settings */}
                                        <Collapse 
                                            in={isMonitoringExpanded} 
                                            timeout="auto" 
                                            unmountOnExit
                                            sx={{
                                                '&.MuiCollapse-entered': {
                                                    overflow: 'visible'
                                                }
                                            }}
                                        >
                                            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', bgcolor: 'rgba(255,255,255,0.01)', p: 1.5 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                                                    {/* Scan Area */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                                                {t('accountPage.threatRadarPrefs.scanArea', 'Scan Area')}
                                                            </Typography>
                                                            <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.scanArea', 'Choose whether to hunt for threats across all public posts, or strictly limit the search to your own secured assets.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <FormControl size="small" variant="outlined" sx={{ width: 220 }}>
                                                            <Select
                                                                value={trPrefs.monitoring_watermarked_only ? 'watermarked' : 'public'}
                                                                onChange={(e) => setTrPrefs(prev => ({...prev, monitoring_watermarked_only: e.target.value === 'watermarked'}))}
                                                                sx={{ 
                                                                    color: 'white', 
                                                                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' }, 
                                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.4)' }, 
                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' },
                                                                    '& .MuiSelect-select': {
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }
                                                                }}
                                                                MenuProps={{ 
                                                                    PaperProps: { 
                                                                        sx: { 
                                                                            bgcolor: '#ffffff', 
                                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                                            minWidth: 'max-content' // Allows the opened menu to fit the full text comfortably
                                                                        } 
                                                                    } 
                                                                }}
                                                            >
                                                                <MenuItem value="public">{t('accountPage.threatRadarPrefs.scanOptions.public', 'Whole Public Internet')}</MenuItem>
                                                                <MenuItem value="watermarked">{t('accountPage.threatRadarPrefs.scanOptions.watermarked', 'My Watermarked Assets Only')}</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                    
                                                    {/* Platforms */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                                                {t('accountPage.threatRadarPrefs.monitoringPlatforms', 'Target Platforms')}
                                                            </Typography>
                                                            <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.platforms', 'Select which social media networks the background scraper will monitor.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <FormControl 
                                                            ref={platformsSelectRef}
                                                            size="small" 
                                                            variant="outlined" 
                                                            sx={{ minWidth: 220, position: 'relative' }}
                                                            onMouseDown={(e) => {
                                                                // The dropdown menu is a DOM child of this FormControl due to disablePortal: true.
                                                                // We must ensure the user is clicking the Select input, NOT the dropdown menu.
                                                                const clickedInsideMenu = platformsMenuRef.current?.contains(e.target) || Boolean(e.target.closest?.('.MuiMenu-paper'));
                                                                
                                                                if (isPlatformsDropdownOpen && platformsSelectRef.current?.contains(e.target) && !clickedInsideMenu) {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    setIsPlatformsDropdownOpen(false);
                                                                }
                                                            }}
                                                        >
                                                            <Select
                                                                multiple
                                                                open={isPlatformsDropdownOpen}
                                                                onOpen={() => setIsPlatformsDropdownOpen(true)}
                                                                onClose={(event, reason) => {
                                                                    if (reason === 'escapeKeyDown') {
                                                                        setIsPlatformsDropdownOpen(false);
                                                                    }
                                                                }}
                                                                value={trPrefs.monitoring_platforms || []}
                                                                onChange={(e) => {
                                                                    const selected = e.target.value;
                                                                    // Prevent deselecting the last platform
                                                                    if (selected.length === 0) return;
                                                                    setTrPrefs(prev => ({...prev, monitoring_platforms: selected}));
                                                                }}
                                                                renderValue={(selected) => selected.length === radarGlobalPlatforms.length ? t('accountPage.threatRadarPrefs.allPlatforms', 'All Platforms') : t('accountPage.threatRadarPrefs.numSelected', { count: selected.length, defaultValue: `${selected.length} Selected` })}
                                                                sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.4)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' } }}
                                                                MenuProps={{ 
                                                                    className: 'history-multi-select-menu',
                                                                    hideBackdrop: true,
                                                                    disableScrollLock: true,
                                                                    disableAutoFocus: true,
                                                                    disableEnforceFocus: true,
                                                                    disablePortal: true, // Keeps dropdown anchored to its parent during scroll
                                                                    sx: { 
                                                                        // Let clicks pass through the invisible Modal wrapper to the sliders
                                                                        pointerEvents: 'none',
                                                                        position: 'absolute !important',
                                                                        top: '0 !important',
                                                                        left: '0 !important',
                                                                        width: '100%'
                                                                        // Removed height: '100%' to prevent it from squishing to the size of the input box
                                                                    },
                                                                    PaperProps: {
                                                                        ref: platformsMenuRef,
                                                                        sx: { 
                                                                            pointerEvents: 'auto',
                                                                            // Force the actual white menu box to anchor directly below the select box
                                                                            position: 'absolute !important',
                                                                            top: 'calc(100% + 4px) !important',
                                                                            left: '0 !important',
                                                                            margin: '0 !important',
                                                                            transform: 'none !important',
                                                                            minWidth: '100%',
                                                                            width: 'max-content !important', // Ensures "Meta (Facebook)" fits on one line
                                                                            maxHeight: '280px !important', // Scrollbar & Max Height
                                                                            overflowY: 'auto !important',
                                                                            zIndex: 9999,
                                                                            boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                {radarGlobalPlatforms.map(plat => {
                                                                    const isSelected = (trPrefs.monitoring_platforms || []).includes(plat);
                                                                    const isLastRemaining = isSelected && (trPrefs.monitoring_platforms || []).length === 1;

                                                                    return (
                                                                        <MenuItem 
                                                                            key={plat} 
                                                                            value={plat}
                                                                            sx={{ 
                                                                                cursor: isLastRemaining ? 'not-allowed' : 'pointer',
                                                                                // Forces the not-allowed cursor to apply to the Checkbox and inner text as well
                                                                                '& *': { cursor: isLastRemaining ? 'not-allowed !important' : 'inherit' }
                                                                            }}
                                                                        >
                                                                            <Tooltip 
                                                                                title={isLastRemaining ? t('accountPage.threatRadarPrefs.minOnePlatform', 'At least 1 platform must be selected') : ""} 
                                                                                placement="left" 
                                                                                arrow 
                                                                                disableHoverListener={!isLastRemaining}
                                                                                PopperProps={{ className: 'global-dark-tooltip' }}
                                                                            >
                                                                                {/* Full-width Box so the tooltip catches the hover anywhere on the row */}
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                                                    <Checkbox checked={isSelected} size="small" />
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                        {PLATFORM_ICONS[plat.toLowerCase()]}
                                                                                        <ListItemText primary={getPlatformDisplayName(plat, t)} />
                                                                                    </Box>
                                                                                </Box>
                                                                            </Tooltip>
                                                                        </MenuItem>
                                                                    );
                                                                })}
                                                            </Select>
                                                        </FormControl>
                                                    </Box>

                                                    {/* Platform Configurations (Dynamic Sub-Menu) */}
                                                    <Collapse in={isPlatformsDropdownOpen && trPrefs.monitoring_platforms?.length > 1} timeout="auto" unmountOnExit>
                                                        <Box 
                                                            ref={platformsWeightsRef}
                                                            sx={{ mt: 1, pl: 3, borderLeft: '2px solid rgba(255,255,255,0.1)' }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block' }}>
                                                                    {t('accountPage.threatRadarPrefs.platformConfigsTitle', 'Platform Configurations')}
                                                                </Typography>
                                                                <Tooltip 
                                                                    title={t('accountPage.threatRadarPrefs.platformConfigsNote', 'Configure the distribution weight and sorting behavior per platform. Tip: Saving a weight of 0 will automatically disable that platform.')} 
                                                                    arrow 
                                                                    placement="right" 
                                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                                >
                                                                    <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                                                </Tooltip>
                                                            </Box>
                                                            
                                                            {(() => {
                                                                // Calculate total weight to display dynamic percentages
                                                                const totalWeight = trPrefs.monitoring_platforms.reduce(
                                                                    (sum, p) => sum + (trPrefs.monitoring_weights?.[p] ?? 0.5), 0
                                                                );

                                                                // Sort platforms to match the global dropdown order instead of click order
                                                                const orderedPlatforms = radarGlobalPlatforms.filter(p => trPrefs.monitoring_platforms.includes(p));

                                                                const numActive = orderedPlatforms.length;

                                                                // Helper for smooth RGB color interpolation
                                                                const interpolateColor = (c1, c2, factor) => {
                                                                    const r = Math.round(c1[0] + factor * (c2[0] - c1[0]));
                                                                    const g = Math.round(c1[1] + factor * (c2[1] - c1[1]));
                                                                    const b = Math.round(c1[2] + factor * (c2[2] - c1[2]));
                                                                    return `rgb(${r}, ${g}, ${b})`;
                                                                };

                                                                return orderedPlatforms.map(plat => {
                                                                    const weight = trPrefs.monitoring_weights?.[plat] ?? 0.5;
                                                                    const percentage = totalWeight > 0 ? (weight / totalWeight) * 100 : 0;
                                                                    const displayPercentage = percentage.toFixed(1);
                                                                    
                                                                    let percentColor;
                                                                    let percentWeight = weight === 0 ? 'bold' : 'normal';
                                                                    
                                                                    if (totalWeight === 0 || weight === 0) {
                                                                        percentColor = '#F44336'; // Pure Red
                                                                    } else {
                                                                        const red = [244, 67, 54];     // #F44336
                                                                        const yellow = [255, 177, 66]; // #FFB142
                                                                        const green = [76, 175, 80];   // #4CAF50
                                                                        
                                                                        // Calculate dynamic boundaries based on the "Max Spread" logic
                                                                        let minCap = 0;
                                                                        let maxCap = 100;
                                                                        let midPoint = 100 / numActive;
                                                                        
                                                                        if (numActive === 1) {
                                                                            minCap = 0;
                                                                            midPoint = 50;
                                                                            maxCap = 100;
                                                                        } else if (numActive >= 2) {
                                                                            // Calculate the theoretical max/min percentages when one is 1.0, one is 0.1, and the rest are default (0.5)
                                                                            const sumMaxScenario = 1.0 + 0.1 + ((numActive - 2) * 0.5);
                                                                            maxCap = (1.0 / sumMaxScenario) * 100;
                                                                            minCap = (0.1 / sumMaxScenario) * 100;
                                                                        }

                                                                        // Clamp the percentage to our calculated caps for the interpolation
                                                                        const clampedPercentage = Math.max(minCap, Math.min(percentage, maxCap));

                                                                        if (clampedPercentage <= midPoint) {
                                                                            // Map minCap -> midPoint into a 0.0 to 1.0 factor (Red to Yellow)
                                                                            const range = midPoint - minCap;
                                                                            const factor = range === 0 ? 1 : (clampedPercentage - minCap) / range;
                                                                            percentColor = interpolateColor(red, yellow, factor);
                                                                        } else {
                                                                            // Map midPoint -> maxCap into a 0.0 to 1.0 factor (Yellow to Green)
                                                                            const range = maxCap - midPoint;
                                                                            const factor = range === 0 ? 1 : (clampedPercentage - midPoint) / range;
                                                                            percentColor = interpolateColor(yellow, green, factor);
                                                                        }
                                                                    }

                                                                    const isSortSupported = SORT_SUPPORTED_PLATFORMS[plat];
                                                                    const currentSort = trPrefs.monitoring_sort_methods?.[plat] || 'popular';
                                                                    
                                                                    return (
                                                                        <Box key={`weight-${plat}`} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1.5, justifyContent: 'flex-start' }}>
                                                                            <Box sx={{ flexShrink: 0 }}>
                                                                                {!isSortSupported ? (
                                                                                    /* Single tooltip on disabled element */
                                                                                    <Tooltip title={t('accountPage.threatRadarPrefs.sortUnsupported', 'Custom sorting is not supported for this platform.')} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                                                        <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>
                                                                                            <ToggleButtonGroup
                                                                                                disabled
                                                                                                size="small"
                                                                                                sx={{ 
                                                                                                    height: '22px',
                                                                                                    '& .MuiToggleButton-root': { 
                                                                                                        color: 'rgba(255, 255, 255, 0.25) !important', 
                                                                                                        borderColor: 'rgba(255, 255, 255, 0.08) !important',
                                                                                                        backgroundColor: 'transparent !important',
                                                                                                        fontSize: '0.65rem',
                                                                                                        padding: '0 8px',
                                                                                                        textTransform: 'uppercase',
                                                                                                        cursor: 'not-allowed !important',
                                                                                                        pointerEvents: 'none'
                                                                                                    }
                                                                                                }}
                                                                                            >
                                                                                                <ToggleButton value="popular">
                                                                                                    {t('accountPage.threatRadarPrefs.sortOptions.popular', 'Pop')}
                                                                                                </ToggleButton>
                                                                                                <ToggleButton value="latest">
                                                                                                    {t('accountPage.threatRadarPrefs.sortOptions.latest', 'New')}
                                                                                                </ToggleButton>
                                                                                            </ToggleButtonGroup>
                                                                                        </span>
                                                                                    </Tooltip>
                                                                                ) : (
                                                                                    /* Active Toggle with Split Tooltips and Hover Highlights */
                                                                                    <ToggleButtonGroup
                                                                                        value={currentSort}
                                                                                        exclusive
                                                                                        size="small"
                                                                                        onChange={(e, newSort) => {
                                                                                            if (newSort !== null) {
                                                                                                setTrPrefs(prev => ({
                                                                                                    ...prev,
                                                                                                    monitoring_sort_methods: {
                                                                                                        ...(prev.monitoring_sort_methods || {}),
                                                                                                        [plat]: newSort
                                                                                                    }
                                                                                                }));
                                                                                            }
                                                                                        }}
                                                                                        sx={{ 
                                                                                            height: '22px',
                                                                                            '& .MuiToggleButton-root': { 
                                                                                                color: 'rgba(255, 255, 255, 0.45)', 
                                                                                                borderColor: 'rgba(255, 255, 255, 0.15)',
                                                                                                fontSize: '0.65rem',
                                                                                                padding: '0 8px',
                                                                                                textTransform: 'uppercase',
                                                                                                transition: 'all 0.2s ease',
                                                                                                '&:hover': {
                                                                                                    backgroundColor: 'rgba(79, 195, 247, 0.15)',
                                                                                                    color: '#ffffff',
                                                                                                    borderColor: 'var(--secondary-color)'
                                                                                                },
                                                                                                '&.Mui-selected': { 
                                                                                                    backgroundColor: 'var(--secondary-color) !important', 
                                                                                                    borderColor: 'var(--secondary-color) !important',
                                                                                                    color: '#ffffff !important',
                                                                                                    fontWeight: 600,
                                                                                                    '&:hover': { 
                                                                                                        backgroundColor: 'var(--btn-hover-color) !important',
                                                                                                        borderColor: 'var(--btn-hover-color) !important'
                                                                                                    }
                                                                                                }
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <Tooltip title={t('accountPage.threatRadarPrefs.sortPopular', 'Prioritizes viral/high-engagement content. Best for catching widespread threats.')} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                                                            <ToggleButton value="popular">
                                                                                                {t('accountPage.threatRadarPrefs.sortOptions.popular', 'Pop')}
                                                                                            </ToggleButton>
                                                                                        </Tooltip>
                                                                                        <Tooltip title={t('accountPage.threatRadarPrefs.sortLatest', 'Prioritizes chronological content. Best for catching new threats instantly.')} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                                                            <ToggleButton value="latest">
                                                                                                {t('accountPage.threatRadarPrefs.sortOptions.latest', 'New')}
                                                                                            </ToggleButton>
                                                                                        </Tooltip>
                                                                                    </ToggleButtonGroup>
                                                                                )}
                                                                            </Box>
                                                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', width: '125px' }}>
                                                                                {getPlatformDisplayName(plat, t)}
                                                                            </Typography>
                                                                            <Slider 
                                                                                value={weight} 
                                                                                onChange={(e, val) => setTrPrefs(prev => {
                                                                                    const seededWeights = { ...(prev.monitoring_weights || {}) };
                                                                                    (prev.monitoring_platforms || []).forEach(p => {
                                                                                        if (seededWeights[p] === undefined) {
                                                                                            seededWeights[p] = 0.5;
                                                                                        }
                                                                                    });
                                                                                    seededWeights[plat] = val;
                                                                                    return {
                                                                                        ...prev,
                                                                                        monitoring_weights: seededWeights
                                                                                    };
                                                                                })}
                                                                                min={0} max={1.0} step={0.01}
                                                                                valueLabelDisplay="auto"
                                                                                valueLabelFormat={(val) => val.toFixed(2)}
                                                                                size="small"
                                                                                sx={{ 
                                                                                    color: 'var(--secondary-color)', 
                                                                                    width: '150px',
                                                                                    '& .MuiSlider-valueLabel': {
                                                                                        backgroundColor: '#1e293b', 
                                                                                        color: 'white',
                                                                                        borderRadius: '4px',
                                                                                        fontSize: '0.75rem',
                                                                                        padding: '2px 6px',
                                                                                        border: '1px solid rgba(255, 255, 255, 0.1)'
                                                                                    }
                                                                                }} 
                                                                            />
                                                                            {/* 4. Percentage */}
                                                                            <Typography variant="caption" sx={{ color: percentColor, width: '42px', textAlign: 'right', fontWeight: percentWeight, flexShrink: 0, transition: 'color 0.1s ease' }}>
                                                                                {displayPercentage}%
                                                                            </Typography>
                                                                        </Box>
                                                                    );
                                                                });
                                                            })()}
                                                        </Box>
                                                    </Collapse>

                                                    {/* Tasks */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                                                {t('accountPage.threatRadarPrefs.monitoringTasks', 'Tasks to Run')}
                                                            </Typography>
                                                            <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.tasks', 'Select which forensic AI models will analyze the scraped content.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <FormControl size="small" variant="outlined" sx={{ minWidth: 220 }}>
                                                            <Select
                                                                multiple
                                                                value={trPrefs.monitoring_tasks || []}
                                                                onChange={(e) => {
                                                                    const selected = e.target.value;
                                                                    // Prevent deselecting the last remaining task
                                                                    if (selected.length === 0) return;
                                                                    setTrPrefs(prev => ({...prev, monitoring_tasks: selected}));
                                                                }}
                                                                renderValue={(selected) => t('accountPage.threatRadarPrefs.numSelected', { count: selected.length, defaultValue: `${selected.length} Selected` })}
                                                                sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.4)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' } }}
                                                                MenuProps={{ className: 'history-multi-select-menu' }}
                                                            >
                                                                {tasksConfig ? Object.entries(tasksConfig)
                                                                    .filter(([key, config]) => !config.is_meta_task)
                                                                    .map(([taskKey, taskData]) => {
                                                                        const isSelected = (trPrefs.monitoring_tasks || []).includes(taskKey);
                                                                        const isLastRemaining = isSelected && (trPrefs.monitoring_tasks || []).length === 1;

                                                                        return (
                                                                            <MenuItem 
                                                                                key={taskKey} 
                                                                                value={taskKey} 
                                                                                sx={{ 
                                                                                    display: 'flex', 
                                                                                    justifyContent: 'space-between',
                                                                                    cursor: isLastRemaining ? 'not-allowed' : 'pointer',
                                                                                    '& *': { cursor: isLastRemaining ? 'not-allowed !important' : 'inherit' }
                                                                                }}
                                                                            >
                                                                                <Tooltip 
                                                                                    title={isLastRemaining ? t('accountPage.threatRadarPrefs.minOneTask', 'At least 1 task must be selected') : ""} 
                                                                                    placement="left" 
                                                                                    arrow 
                                                                                    disableHoverListener={!isLastRemaining}
                                                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                                                >
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                            <Checkbox checked={isSelected} size="small" />
                                                                                            {getTaskDisplayName(taskKey, taskData.display_name)}
                                                                                        </Box>
                                                                                        <Tooltip title={t(`verificationPage.scoreDefinitions.${taskKey}`, { defaultValue: taskData.description || '' })} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                                                            <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(0,0,0,0.4)', ml: 1, '&:hover': { color: 'rgba(0,0,0,0.8)' } }} />
                                                                                        </Tooltip>
                                                                                    </Box>
                                                                                </Tooltip>
                                                                            </MenuItem>
                                                                        );
                                                                    }) : null}
                                                            </Select>
                                                        </FormControl>
                                                    </Box>

                                                    {/* Max Posts Slider */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                                                {t('accountPage.threatRadarPrefs.monitoringMaxPosts', 'Max Posts per Scrape:')}{' '}
                                                                <span style={{ 
                                                                    color: isMaxPostsHighlighted ? 'var(--secondary-color)' : 'inherit', 
                                                                    fontWeight: isMaxPostsHighlighted ? 600 : 'inherit',
                                                                    transition: 'color 0.3s ease' 
                                                                }}>
                                                                    {trPrefs.monitoring_max_posts || 50}
                                                                </span>
                                                            </Typography>
                                                            <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.maxPosts', 'Higher limits find more threats but consume more Compute Units per run.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <Box sx={{ width: '220px', mr: 0 }}>
                                                            <Slider 
                                                                value={trPrefs.monitoring_max_posts || 50} 
                                                                onChange={(e, val) => {
                                                                    setTrPrefs(prev => ({ ...prev, monitoring_max_posts: val }));
                                                                    setIsMaxPostsHighlighted(true);
                                                                    if (maxPostsTimeoutRef.current) clearTimeout(maxPostsTimeoutRef.current);
                                                                    maxPostsTimeoutRef.current = setTimeout(() => {
                                                                        setIsMaxPostsHighlighted(false);
                                                                    }, 2500);
                                                                }} 
                                                                min={1} 
                                                                max={radarGlobalMaxPosts} 
                                                                valueLabelDisplay="auto"
                                                                sx={{ 
                                                                    color: 'var(--secondary-color)',
                                                                    '& .MuiSlider-valueLabel': {
                                                                        backgroundColor: '#1e293b', 
                                                                        color: 'white',
                                                                        borderRadius: '4px',
                                                                        fontSize: '0.75rem',
                                                                        padding: '2px 6px',
                                                                        border: '1px solid rgba(255, 255, 255, 0.1)'
                                                                    }
                                                                }} 
                                                            />
                                                        </Box>
                                                    </Box>

                                                    {/* Cadence */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                                                {t('accountPage.threatRadarPrefs.monitoringCadence', 'Scanning Schedule')}
                                                            </Typography>
                                                            <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.cadence', 'How often the background radar activates.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <FormControl size="small" variant="outlined" sx={{ minWidth: 220 }}>
                                                            <Select
                                                                value={trPrefs.monitoring_cadence || 'daily'}
                                                                onChange={(e) => setTrPrefs(prev => ({...prev, monitoring_cadence: e.target.value}))}
                                                                sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.4)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' } }}
                                                                MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', color: 'rgba(0, 0, 0, 0.87)' } } }}
                                                            >
                                                                {/* TEST OPTIONS - AUTOMATICALLY HIDDEN IN PRODUCTION */}
                                                                {isLocalDev && [
                                                                    <MenuItem key="test-1" value="1_min" sx={{ color: '#ffb142' }}>{t('accountPage.threatRadarPrefs.cadenceOptions.test1Min', '[TEST] Every 1 Minute')}</MenuItem>,
                                                                    <MenuItem key="test-5" value="5_min" sx={{ color: '#ffb142' }}>{t('accountPage.threatRadarPrefs.cadenceOptions.test5Min', '[TEST] Every 5 Minutes')}</MenuItem>
                                                                ]}
                                                                {/* ---------------------------------- */}
                                                                <MenuItem value="hourly">{t('accountPage.threatRadarPrefs.cadenceOptions.hourly', 'Hourly')}</MenuItem>
                                                                <MenuItem value="daily">{t('accountPage.threatRadarPrefs.cadenceOptions.daily', 'Daily')}</MenuItem>
                                                                <MenuItem value="weekly">{t('accountPage.threatRadarPrefs.cadenceOptions.weekly', 'Weekly')}</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Box>

                                                    {/* Deterministic Schedule Explainer */}
                                                    {user?.id && (
                                                        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'rgba(79, 195, 247, 0.1)', borderRadius: 1, borderLeft: '3px solid #4fc3f7' }}>
                                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                                                {(() => {
                                                                    const minute = (user.id % 60).toString().padStart(2, '0');
                                                                    const hour = (Math.floor(user.id / 60) % 24).toString().padStart(2, '0');
                                                                    const dayNum = user.id % 7;
                                                                    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                                                                    const day = days[dayNum];
                                                                    const cadence = trPrefs.monitoring_cadence || 'daily';
                                                                    
                                                                    if (cadence === '1_min' || cadence === '5_min') {
                                                                        return t('accountPage.threatRadarPrefs.testModeWarning', 'TEST MODE: Bypassing load-balancer. Running immediately.');
                                                                    }
                                                                    if (cadence === 'hourly') {
                                                                        return t('accountPage.threatRadarPrefs.scheduleExplainerHourly', {
                                                                            minute,
                                                                            defaultValue: 'To prevent network bottlenecks, your automated scans are securely load-balanced and scheduled to run at exactly XX:{{minute}} every hour.'
                                                                        });
                                                                    }
                                                                    if (cadence === 'daily') {
                                                                        return t('accountPage.threatRadarPrefs.scheduleExplainerDaily', {
                                                                            hour,
                                                                            minute,
                                                                            defaultValue: 'To prevent network bottlenecks, your automated daily scans are load-balanced and securely scheduled for approximately {{hour}}:{{minute}} UTC.'
                                                                        });
                                                                    }
                                                                    return t('accountPage.threatRadarPrefs.scheduleExplainerWeekly', {
                                                                        day,
                                                                        hour,
                                                                        minute,
                                                                        defaultValue: 'To prevent network bottlenecks, your automated weekly scans are securely scheduled to run every {{day}} at approximately {{hour}}:{{minute}} UTC.'
                                                                    });
                                                                })()}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                </Box>
                                            </Box>
                                        </Collapse>
                                    </Box>

                                    {/* Automated Takedowns */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                                                {t('accountPage.threatRadarPrefs.automatedTakedowns', 'Fully Automated Takedowns')}
                                            </Typography>
                                            <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.automatedTakedowns', 'If enabled, the system will automatically dispatch DMCA takedown notices for threats scoring above your maximum threshold without requiring 1-click manual approval.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                            </Tooltip>
                                            <ProactiveScopeIcon />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Tooltip 
                                                title={hasNoKeywords ? t('accountPage.threatRadarPrefs.tooltips.requiresKeywords', 'Add at least 1 keyword to enable this feature.') : ''} 
                                                arrow 
                                                placement="top" 
                                                PopperProps={{ className: 'global-dark-tooltip' }}
                                            >
                                                <span 
                                                    onMouseEnter={() => hasNoKeywords && setIsHoveringDisabledSwitch(true)}
                                                    onMouseLeave={() => setIsHoveringDisabledSwitch(false)}
                                                    style={{ cursor: hasNoKeywords ? 'not-allowed' : 'pointer', display: 'inline-flex' }}
                                                >
                                                    <Switch
                                                        className="custom-explanation-switch"
                                                        size="small"
                                                        checked={trPrefs.automated_takedowns || false}
                                                        disabled={hasNoKeywords}
                                                        sx={{ pointerEvents: hasNoKeywords ? 'none' : 'auto' }}
                                                        onChange={(e) => setTrPrefs(prev => ({...prev, automated_takedowns: e.target.checked}))}
                                                    />
                                                </span>
                                            </Tooltip>
                                            {/* Invisible placeholder to perfectly align switch with the ExpandMoreIcon buttons above */}
                                            <Box sx={{ width: '28px' }} />
                                        </Box>
                                    </Box>

                                    {/* --- Email Notification Group --- */}
                                    <Box 
                                        sx={{ 
                                            bgcolor: 'rgba(255,255,255,0.02)', 
                                            p: 0, // Padding handled by internal boxes
                                            borderRadius: 1, 
                                            border: '1px solid rgba(255,255,255,0.05)', 
                                            mt: 0,
                                            overflow: 'hidden',
                                            transition: 'border 0.3s ease, background-color 0.3s ease' // Removed 'all' to prevent height conflicts with Collapse
                                        }}
                                    >
                                        {/* Master Switch Row */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: 500 }}>
                                                    {t('accountPage.threatRadarPrefs.emailAlertsEnabledLabel')}
                                                </Typography>
                                                <Tooltip title={t('accountPage.threatRadarPrefs.emailAlertsEnabledTooltip')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                    <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                                </Tooltip>
                                                <ProactiveScopeIcon />
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Tooltip 
                                                    title={hasNoKeywords ? t('accountPage.threatRadarPrefs.tooltips.requiresKeywords', 'Add at least 1 keyword to enable this feature.') : ''} 
                                                    arrow 
                                                    placement="top" 
                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                >
                                                    <span 
                                                        onMouseEnter={() => hasNoKeywords && setIsHoveringDisabledSwitch(true)}
                                                        onMouseLeave={() => setIsHoveringDisabledSwitch(false)}
                                                        style={{ cursor: hasNoKeywords ? 'not-allowed' : 'pointer', display: 'inline-flex' }}
                                                    >
                                                        <Switch
                                                            className="custom-explanation-switch"
                                                            size="small"
                                                            checked={trPrefs.email_alerts_enabled || false}
                                                            disabled={hasNoKeywords}
                                                            sx={{ pointerEvents: hasNoKeywords ? 'none' : 'auto' }}
                                                            onChange={(e) => {
                                                                const isChecked = e.target.checked;
                                                                setTrPrefs(prev => ({...prev, email_alerts_enabled: isChecked}));
                                                                setIsEmailExpanded(isChecked);
                                                            }}
                                                        />
                                                    </span>
                                                </Tooltip>
                                                <IconButton
                                                    size="small" 
                                                    onClick={() => setIsEmailExpanded(!isEmailExpanded)}
                                                    sx={{ 
                                                        color: 'rgba(255,255,255,0.6)', 
                                                        transform: isEmailExpanded ? 'rotate(180deg)' : 'rotate(0deg)', 
                                                        transition: 'transform 0.3s ease',
                                                        padding: '2px'
                                                    }}
                                                >
                                                    <ExpandMoreIcon />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        {/* Sub-Settings (Smoothly expands downward) */}
                                        <Collapse in={isEmailExpanded} timeout="auto" unmountOnExit>
                                            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', bgcolor: 'rgba(255,255,255,0.01)', p: 1.5 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    
                                                    {/* Alert Frequency */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                                                {t('accountPage.threatRadarPrefs.emailFrequencyLabel')}
                                                            </Typography>
                                                            <Tooltip
                                                                title={t('accountPage.threatRadarPrefs.emailFrequencyTooltip')} 
                                                                arrow 
                                                                placement="right" 
                                                                PopperProps={{ className: 'global-dark-tooltip' }}
                                                            >
                                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <FormControl size="small" variant="outlined" sx={{ minWidth: 220 }}>
                                                            <Select
                                                                value={trPrefs.email_cadence || 'weekly'}
                                                                onChange={(e) => setTrPrefs(prev => ({...prev, email_cadence: e.target.value}))}
                                                                sx={{ 
                                                                    color: 'white', 
                                                                    fontWeight: 500, // Makes the collapsed text more visible
                                                                    bgcolor: 'rgba(255,255,255,0.05)', // Adds subtle contrast
                                                                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' },
                                                                    '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' }
                                                                }}
                                                                MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', color: 'rgba(0, 0, 0, 0.87)' } } }}
                                                            >
                                                                {/* TEST OPTIONS - AUTOMATICALLY HIDDEN IN PRODUCTION */}
                                                                {isLocalDev && [
                                                                    <MenuItem key="test-1" value="1_min" sx={{ color: '#ffb142' }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                            {t('accountPage.threatRadarPrefs.cadenceOptions.test1Min', '[TEST] Every 1 Minute')}
                                                                        </Box>
                                                                    </MenuItem>,
                                                                    <MenuItem key="test-5" value="5_min" sx={{ color: '#ffb142' }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                            {t('accountPage.threatRadarPrefs.cadenceOptions.test5Min', '[TEST] Every 5 Minutes')}
                                                                        </Box>
                                                                    </MenuItem>
                                                                ]}
                                                                {/* ---------------------------------- */}
                                                                
                                                                <MenuItem value="hourly">
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                        {t('accountPage.threatRadarPrefs.cadenceOptions.hourly')}
                                                                        <Tooltip title={t('accountPage.threatRadarPrefs.cadenceTooltips.hourly')} arrow placement="right">
                                                                            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(0,0,0,0.3)', ml: 1 }} />
                                                                        </Tooltip>
                                                                    </Box>
                                                                </MenuItem>
                                                                <MenuItem value="daily">
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                        {t('accountPage.threatRadarPrefs.cadenceOptions.daily')}
                                                                        <Tooltip title={t('accountPage.threatRadarPrefs.cadenceTooltips.daily')} arrow placement="right">
                                                                            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(0,0,0,0.3)', ml: 1 }} />
                                                                        </Tooltip>
                                                                    </Box>
                                                                </MenuItem>
                                                                <MenuItem value="weekly">
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                        {t('accountPage.threatRadarPrefs.cadenceOptions.weekly')}
                                                                        <Tooltip title={t('accountPage.threatRadarPrefs.cadenceTooltips.weekly')} arrow placement="right">
                                                                            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(0,0,0,0.3)', ml: 1 }} />
                                                                        </Tooltip>
                                                                    </Box>
                                                                </MenuItem>
                                                                <MenuItem value="monthly">
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                        {t('accountPage.threatRadarPrefs.cadenceOptions.monthly')}
                                                                        <Tooltip title={t('accountPage.threatRadarPrefs.cadenceTooltips.monthly')} arrow placement="right">
                                                                            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(0,0,0,0.3)', ml: 1 }} />
                                                                        </Tooltip>
                                                                    </Box>
                                                                </MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Box>

                                                    {/* Include Previously Alerted Threats */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                                                {t('accountPage.threatRadarPrefs.emailIncludeDuplicatesLabel')}
                                                            </Typography>
                                                            <Tooltip title={t('accountPage.threatRadarPrefs.emailIncludeDuplicatesTooltip')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <Switch
                                                            className="custom-explanation-switch"
                                                            size="small"
                                                            checked={trPrefs.email_include_duplicates || false}
                                                            onChange={(e) => setTrPrefs(prev => ({...prev, email_include_duplicates: e.target.checked}))}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Collapse>
                                    </Box>

                                    {/* Score Range */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                                                {t('accountPage.threatRadarPrefs.threatScoreRangeLabel', 'Threat Score Range:')}{' '}
                                                <span style={{ color: activeScoreThumb === 'min' ? 'var(--secondary-color)' : 'inherit', transition: 'color 0.3s ease' }}>
                                                    {trPrefs.default_score_range[0]}%
                                                </span>
                                                {' - '}
                                                <span style={{ color: activeScoreThumb === 'max' ? 'var(--secondary-color)' : 'inherit', transition: 'color 0.3s ease' }}>
                                                    {trPrefs.default_score_range[1]}%
                                                </span>
                                            </Typography>
                                            <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.scoreRange')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                            </Tooltip>
                                            <DashboardScopeIcon />
                                        </Box>
                                        <Box sx={{ width: '205px', mr: 1 }}>
                                            <Slider 
                                                value={trPrefs.default_score_range} 
                                                onChange={(e, val, activeThumb) => {
                                                    setTrPrefs(prev => ({...prev, default_score_range: val}));
                                                    setActiveScoreThumb(activeThumb === 0 ? 'min' : 'max');
                                                    if (scoreTimeoutRef.current) clearTimeout(scoreTimeoutRef.current);
                                                    scoreTimeoutRef.current = setTimeout(() => {
                                                        setActiveScoreThumb(null);
                                                    }, 2500);
                                                }}
                                                min={0} 
                                                max={100} 
                                                valueLabelDisplay="auto"
                                                valueLabelFormat={(val) => `${val}%`}
                                                sx={{ 
                                                    color: 'var(--secondary-color)',
                                                    '& .MuiSlider-valueLabel': {
                                                        backgroundColor: '#1e293b', 
                                                        color: 'white',
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        padding: '2px 6px',
                                                        border: '1px solid rgba(255, 255, 255, 0.1)'
                                                    }
                                                }} 
                                            />
                                        </Box>
                                    </Box>

                                    {/* Source Type */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                                                {t('accountPage.threatRadarPrefs.defaultSourceTypes')}
                                            </Typography>
                                            <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.sourceTypes')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                            </Tooltip>
                                            <DashboardScopeIcon />
                                        </Box>
                                        <FormControl size="small" variant="outlined" sx={{ minWidth: 220 }}>
                                            <Select
                                                multiple
                                                value={trPrefs.default_source_filter}
                                                onChange={(e) => {
                                                    const selected = e.target.value;
                                                    if (selected.length === 0) return; // Prevent deselecting last option
                                                    setTrPrefs(prev => ({...prev, default_source_filter: selected}))
                                                }}
                                                renderValue={(selected) => {
                                                    if (selected.length === 3) return t('accountPage.threatRadarPrefs.sourceOptions.all', 'All Sources');
                                                    if (selected.length === 2) return t('accountPage.threatRadarPrefs.numSelected', { count: 2, defaultValue: '2 Selected' });
                                                    if (selected[0] === 'scraped') return t('accountPage.threatRadarPrefs.sourceOptions.scraped', 'Automated Social Scrapes (Global)');
                                                    if (selected[0] === 'public') return t('accountPage.threatRadarPrefs.sourceOptions.public', 'Public URLs (Global)');
                                                    return t('accountPage.threatRadarPrefs.sourceOptions.local', 'My Local Uploads (Private)');
                                                }}
                                                sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' } }}
                                                MenuProps={{ className: 'history-multi-select-menu' }}
                                            >
                                                <MenuItem value="scraped" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Checkbox checked={trPrefs.default_source_filter.includes('scraped')} size="small" />
                                                        <ListItemText primary={t('accountPage.threatRadarPrefs.sourceOptions.scraped', 'Automated Social Scrapes')} />
                                                    </Box>
                                                    <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.sourceScraped', 'Threats found autonomously by the background Radar.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                        <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(0,0,0,0.4)', ml: 1, '&:hover': { color: 'rgba(0,0,0,0.8)' } }} />
                                                    </Tooltip>
                                                </MenuItem>
                                                <MenuItem value="public" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Checkbox checked={trPrefs.default_source_filter.includes('public')} size="small" />
                                                        <ListItemText primary={t('accountPage.threatRadarPrefs.sourceOptions.public', 'Direct Public URLs')} />
                                                    </Box>
                                                    <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.sourcePublic', 'Threats analyzed manually by pasting a public link into the Analyzer.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                        <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(0,0,0,0.4)', ml: 1, '&:hover': { color: 'rgba(0,0,0,0.8)' } }} />
                                                    </Tooltip>
                                                </MenuItem>
                                                <MenuItem value="local" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Checkbox checked={trPrefs.default_source_filter.includes('local')} size="small" />
                                                        <ListItemText primary={t('accountPage.threatRadarPrefs.sourceOptions.local', 'My Local Uploads')} />
                                                    </Box>
                                                    <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.sourceLocal', 'Private files uploaded directly from your computer.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                        <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(0,0,0,0.4)', ml: 1, '&:hover': { color: 'rgba(0,0,0,0.8)' } }} />
                                                    </Tooltip>
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    {/* Default Scan Area */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                                                {t('accountPage.threatRadarPrefs.defaultScanArea', 'Default Scan Area')}
                                            </Typography>
                                            <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.defaultScanArea', 'Choose whether Threat Radar searches across all public posts, or strictly limit the search to your own secured assets by default.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                            </Tooltip>
                                            <DashboardScopeIcon />
                                        </Box>
                                        <FormControl size="small" variant="outlined" sx={{ minWidth: 220 }}>
                                            <Select
                                                value={trPrefs.watermarked_only ? 'watermarked' : 'public'}
                                                onChange={(e) => setTrPrefs(prev => ({...prev, watermarked_only: e.target.value === 'watermarked'}))}
                                                sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' } }}
                                                MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', color: 'rgba(0, 0, 0, 0.87)' } } }}
                                            >
                                                <MenuItem value="public">{t('accountPage.threatRadarPrefs.scanOptions.public', 'Whole Public Internet')}</MenuItem>
                                                <MenuItem value="watermarked">{t('accountPage.threatRadarPrefs.scanOptions.watermarked', 'My Watermarked Assets Only')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    {/* Default Platforms (Dashboard) */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                                                {t('accountPage.threatRadarPrefs.defaultPlatforms', 'Default Platforms')}
                                            </Typography>
                                            <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.defaultPlatforms', 'Select which platforms appear by default in the Threat Radar dashboard.')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                            </Tooltip>
                                            <DashboardScopeIcon />
                                        </Box>
                                        <FormControl size="small" variant="outlined" sx={{ minWidth: 220 }}>
                                            <Select
                                                multiple
                                                value={trPrefs.default_platforms || ALL_DASHBOARD_PLATFORMS}
                                                onChange={(e) => {
                                                    const selected = e.target.value;
                                                    if (selected.length === 0) return;
                                                    setTrPrefs(prev => ({...prev, default_platforms: selected}));
                                                }}
                                                renderValue={(selected) => selected.length === ALL_DASHBOARD_PLATFORMS.length ? t('accountPage.threatRadarPrefs.allPlatforms', 'All Platforms') : t('accountPage.threatRadarPrefs.numSelected', { count: selected.length, defaultValue: `${selected.length} Selected` })}
                                                sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' } }}
                                                MenuProps={{ className: 'history-multi-select-menu' }}
                                            >
                                                {sortedDashboardPlatforms.map(plat => {
                                                    const isSelected = (trPrefs.default_platforms || ALL_DASHBOARD_PLATFORMS).includes(plat);
                                                    const isLastRemaining = isSelected && (trPrefs.default_platforms || ALL_DASHBOARD_PLATFORMS).length === 1;

                                                    return (
                                                        <MenuItem 
                                                            key={plat} 
                                                            value={plat}
                                                            sx={{ 
                                                                cursor: isLastRemaining ? 'not-allowed' : 'pointer',
                                                                '& *': { cursor: isLastRemaining ? 'not-allowed !important' : 'inherit' }
                                                            }}
                                                        >
                                                            <Tooltip 
                                                                title={isLastRemaining ? t('accountPage.threatRadarPrefs.minOnePlatform', 'At least 1 platform must be selected') : ""} 
                                                                placement="left" 
                                                                arrow 
                                                                disableHoverListener={!isLastRemaining}
                                                                PopperProps={{ className: 'global-dark-tooltip' }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                                    <Checkbox checked={isSelected} size="small" />
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        {PLATFORM_ICONS[plat.toLowerCase()]}
                                                                        <ListItemText primary={getPlatformDisplayName(plat, t)} />
                                                                    </Box>
                                                                </Box>
                                                            </Tooltip>
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    {/* Content Types */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                                                {t('accountPage.threatRadarPrefs.defaultContentTypes')}
                                            </Typography>
                                            <Tooltip title={t('accountPage.threatRadarPrefs.tooltips.contentTypes')} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                            </Tooltip>
                                            <DashboardScopeIcon />
                                        </Box>
                                        <FormControl size="small" variant="outlined" sx={{ minWidth: 220 }}>
                                            <Select
                                                multiple
                                                value={trPrefs.default_content_types}
                                                onChange={(e) => setTrPrefs(prev => ({...prev, default_content_types: e.target.value}))}
                                                renderValue={(selected) => selected.length === 4 ? t('historyFilters.contentType.options.allContentTypes') : selected.map(s => t(`selector.contentType.${s}`)).join(', ')}
                                                sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' } }}
                                                MenuProps={{ className: 'history-multi-select-menu' }}
                                            >
                                                {ORDERED_CONTENT_TYPES.map(opt => (
                                                    <MenuItem key={opt} value={opt}>
                                                        <Checkbox checked={trPrefs.default_content_types.includes(opt)} size="small" />
                                                        <ListItemText primary={t(`selector.contentType.${opt}`)} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    {/* Tasks */}
                                    <Accordion className="pref-accordion" disableGutters sx={{ mt: 1, border: '1px solid rgba(255,255,255,0.05) !important' }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.95rem' }}>
                                                    {t('accountPage.threatRadarPrefs.defaultTasks')}
                                                </Typography>
                                                <Tooltip 
                                                    title={t('accountPage.threatRadarPrefs.tooltips.analysisScores', 'Choose which forensic analysis tasks appear in the Threat Radar filter menu and which ones are enabled by default.')} 
                                                    arrow 
                                                    placement="right" 
                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                >
                                                    <InfoOutlinedIcon 
                                                        onClick={(e) => e.stopPropagation()} 
                                                        sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} 
                                                    />
                                                </Tooltip>
                                                <DashboardScopeIcon />
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: -1 }}>
                                                <Button 
                                                    size="small"
                                                    startIcon={<HelpOutlineIcon sx={{ fontSize: '1.1rem !important' }} />}
                                                    onClick={() => openDocViewer('reference/supported_analysis_tasks.md')}
                                                    sx={{ 
                                                        color: 'var(--secondary-color)', textTransform: 'none', fontSize: '0.8rem',
                                                        fontWeight: 600, letterSpacing: '0.3px', py: 0.5, px: 1, borderRadius: '6px',
                                                        transition: 'all 0.2s ease', '&:hover': { background: 'rgba(79, 195, 247, 0.1)', color: '#fff' }
                                                    }}
                                                >
                                                    {t('accountPage.analysisPrefs.readDocsTooltip')}
                                                </Button>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                {tasksConfig ? Object.entries(tasksConfig)
                                                    .filter(([key, config]) => !config.is_meta_task)
                                                    .map(([taskKey, taskData]) => {
                                                        const isDisplayed = (trPrefs.displayed_tasks || []).includes(taskKey);
                                                        const isDefault = (trPrefs.default_tasks || []).includes(taskKey);
                                                        const displayName = getTaskDisplayName(taskKey, taskData.display_name);
                                                        const tooltipDescription = t(`verificationPage.scoreDefinitions.${taskKey}`, { defaultValue: taskData.description || '' });

                                                        return (
                                                            <Box key={taskKey} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox 
                                                                            size="small" checked={isDisplayed} 
                                                                            onChange={(e) => {
                                                                                const newArr = e.target.checked 
                                                                                    ? [...(trPrefs.displayed_tasks || []), taskKey] 
                                                                                    : (trPrefs.displayed_tasks || []).filter(item => item !== taskKey);
                                                                                
                                                                                setTrPrefs(prev => {
                                                                                    const next = { ...prev, displayed_tasks: newArr };
                                                                                    if (!e.target.checked) {
                                                                                        next.default_tasks = (next.default_tasks || []).filter(item => item !== taskKey);
                                                                                    }
                                                                                    return next;
                                                                                });
                                                                            }} 
                                                                            sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: 'var(--secondary-color)' } }}
                                                                        />
                                                                    }
                                                                    label={
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                            <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>{displayName}</span>
                                                                            <Tooltip title={tooltipDescription} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                                                <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.8)', cursor: 'help' } }} />
                                                                            </Tooltip>
                                                                        </Box>
                                                                    }
                                                                />
                                                                <Tooltip 
                                                                    title={!isDisplayed ? t('accountPage.analysisPrefs.enableToSetDefault') : ""} 
                                                                    arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}
                                                                    disableHoverListener={isDisplayed}
                                                                >
                                                                    <span style={{ cursor: !isDisplayed ? 'not-allowed' : 'default' }}>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Switch 
                                                                                    className="custom-explanation-switch" size="small" 
                                                                                    checked={isDefault && isDisplayed} disabled={!isDisplayed} 
                                                                                    onChange={(e) => {
                                                                                        const newArr = e.target.checked 
                                                                                            ? [...(trPrefs.default_tasks || []), taskKey] 
                                                                                            : (trPrefs.default_tasks || []).filter(item => item !== taskKey);
                                                                                        setTrPrefs(prev => ({ ...prev, default_tasks: newArr }));
                                                                                    }} 
                                                                                />
                                                                            }
                                                                            label={<span style={{ color: !isDisplayed ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>{t('accountPage.analysisPrefs.enabledByDefault')}</span>}
                                                                            labelPlacement="start" sx={{ m: 0, pointerEvents: !isDisplayed ? 'none' : 'auto' }}
                                                                        />
                                                                    </span>
                                                                </Tooltip>
                                                            </Box>
                                                        );
                                                }) : <CircularProgress size={24} sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>

                                    {/* Date Range */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                                                {t('accountPage.threatRadarPrefs.dateRangeLabel', 'Default Date Range')}
                                            </Typography>
                                            <Tooltip 
                                                title={t('accountPage.threatRadarPrefs.tooltips.dateRange', 'Set the default time window used when scanning the database for threats.')} 
                                                arrow 
                                                placement="right" 
                                                PopperProps={{ className: 'global-dark-tooltip' }}
                                            >
                                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', cursor: 'help', '&:hover': { color: 'rgba(255,255,255,0.8)' } }} />
                                            </Tooltip>
                                            <DashboardScopeIcon />
                                        </Box>
                                        <FormControl size="small" variant="outlined" sx={{ minWidth: 220 }}>
                                            <Select
                                                value={trPrefs.default_date_range}
                                                onChange={(e) => setTrPrefs(prev => ({...prev, default_date_range: e.target.value}))}
                                                sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' } }}
                                                MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', color: 'rgba(0, 0, 0, 0.87)' } } }}
                                            >
                                                <MenuItem value="all_time">{t('accountPage.threatRadarPrefs.dateRangeOptions.all_time', 'All Time')}</MenuItem>
                                                <MenuItem value="last_30_days">{t('accountPage.threatRadarPrefs.dateRangeOptions.last_30_days', 'Last 30 Days')}</MenuItem>
                                                <MenuItem value="last_7_days">{t('accountPage.threatRadarPrefs.dateRangeOptions.last_7_days', 'Last 7 Days')}</MenuItem>
                                                <MenuItem value="last_24_hours">{t('accountPage.threatRadarPrefs.dateRangeOptions.last_24_hours', 'Last 24 Hours')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    
                                    {/* Reset Button */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
                                        <Button 
                                            variant="text" 
                                            size="small" 
                                            startIcon={<ReplayIcon />}
                                            onClick={() => setTrPrefs(getMergedTrPrefs({}))}
                                            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.05)' } }}
                                        >
                                            {t('accountPage.threatRadarPrefs.resetDefaults')}
                                        </Button>
                                    </Box>

                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        {/* Accordion: Content Types */}
                        <Accordion className="pref-accordion" expanded={expandedPanels.panel2} onChange={handleAccordionChange('panel2')} disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <PermMediaIcon sx={{ color: 'var(--secondary-color)', fontSize: '1.3rem' }} />
                                    <Typography className="pref-group-title" sx={{ mb: 0, borderBottom: 'none', pb: 0 }}>
                                        {t('accountPage.analysisPrefs.contentTypesTitle')}
                                    </Typography>
                                    {renderInlineSaveButton('panel2')}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <div className="pref-row" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: '1.5rem' }}>
                                    <Box sx={{ width: '100%' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1, display: 'block' }}>
                                            {t('accountPage.analysisPrefs.displayedContentTypes')}
                                        </Typography>
                                        <FormGroup row>
                                            {ORDERED_CONTENT_TYPES.map(ct => (
                                                <FormControlLabel
                                                    key={ct}
                                                    control={
                                                        <Checkbox 
                                                            size="small" 
                                                            checked={analysisPrefs.displayed_content_types.includes(ct)} 
                                                            onChange={(e) => {
                                                                const newArr = e.target.checked 
                                                                    ? [...analysisPrefs.displayed_content_types, ct] 
                                                                    : analysisPrefs.displayed_content_types.filter(item => item !== ct);
                                                                handleAnalysisPrefChange('displayed_content_types', newArr);
                                                            }} 
                                                            sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: 'var(--secondary-color)' } }}
                                                        />
                                                    }
                                                    label={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>{t(`selector.contentType.${ct}`)}</span>}
                                                />
                                            ))}
                                        </FormGroup>
                                    </Box>
                                    <Box sx={{ width: '100%' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1, display: 'block' }}>
                                            {t('accountPage.analysisPrefs.defaultContentType')}
                                        </Typography>
                                        <FormControl size="small" variant="outlined" sx={{ minWidth: 220 }}>
                                            <Select
                                                value={analysisPrefs.default_content_type}
                                                onChange={(e) => handleAnalysisPrefChange('default_content_type', e.target.value)}
                                                sx={{
                                                    color: 'white',
                                                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' },
                                                    '.MuiSvgIcon-root': { color: 'white' }
                                                }}
                                                MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', color: 'rgba(0, 0, 0, 0.87)' } } }}
                                            >
                                                {ORDERED_CONTENT_TYPES.filter(ct => analysisPrefs.displayed_content_types.includes(ct)).map(ct => (
                                                    <MenuItem key={ct} value={ct}>{t(`selector.contentType.${ct}`)}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </div>
                            </AccordionDetails>
                        </Accordion>

                        {/* Accordion: Analysis Tasks*/}
                        <Accordion className="pref-accordion" expanded={expandedPanels.panel3} onChange={handleAccordionChange('panel3')} disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <PsychologyIcon sx={{ color: 'var(--secondary-color)', fontSize: '1.3rem' }} />
                                    <Typography className="pref-group-title" sx={{ mb: 0, borderBottom: 'none', pb: 0 }}>
                                        {t('accountPage.analysisPrefs.tasksTitle')}
                                    </Typography>
                                    {renderInlineSaveButton('panel3')}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {/* Layout container for Left and Right actions */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: -1 }}>
                                    {/* Documentation Link on the LEFT */}
                                    <Button 
                                        size="small"
                                        startIcon={<HelpOutlineIcon sx={{ fontSize: '1.1rem !important' }} />}
                                        onClick={() => openDocViewer('reference/supported_analysis_tasks.md')}
                                        sx={{ 
                                            color: 'var(--secondary-color)', 
                                            textTransform: 'none', 
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            letterSpacing: '0.3px',
                                            py: 0.5,
                                            px: 1,
                                            borderRadius: '6px',
                                            transition: 'all 0.2s ease',
                                            '&:hover': { 
                                                background: 'rgba(79, 195, 247, 0.1)', // Subtle tint
                                                color: '#fff', // Brighter text on hover
                                            }
                                        }}
                                    >
                                        {t('accountPage.analysisPrefs.readDocsTooltip')}
                                    </Button>

                                    {/* "Help me choose?" Button on the RIGHT */}
                                    <Button 
                                        variant="text" 
                                        size="small" 
                                        onClick={() => setIsOnboardingModalOpen(true)}
                                        sx={{ 
                                            color: 'rgba(255,255,255,0.4)', 
                                            textTransform: 'none', 
                                            p: 0, 
                                            fontSize: '0.8rem', 
                                            minWidth: 'auto', 
                                            transition: 'color 0.2s ease',
                                            '&:hover': { background: 'transparent', color: 'rgba(255,255,255,0.8)' } 
                                        }}
                                    >
                                        {t('selector.helpMeChoose')}
                                    </Button>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {tasksConfig ? Object.entries(tasksConfig)
                                        .filter(([key, config]) => !config.is_meta_task)
                                        .map(([taskKey, taskData]) => {
                                            const isDisplayed = (analysisPrefs.displayed_tasks || []).includes(taskKey);
                                            const isDefault = (analysisPrefs.default_tasks || []).includes(taskKey);
                                            
                                            // --- BULLETPROOF TRANSLATION HELPER ---
                                            const camelCaseKey = taskKey.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
                                            
                                            // Array of every possible path your JSON might be using
                                            const possiblePaths = [
                                                `selector.analysisTasks.${taskKey}`,     // Expected new format
                                                `selector.analysisTasks.${camelCaseKey}`, // Legacy format
                                                `analysisTasks.${taskKey}`,              // Un-nested new format
                                                `analysisTasks.${camelCaseKey}`          // Un-nested legacy format
                                            ];
                                            
                                            let displayName = taskData.display_name; // Fallback to YAML
                                            
                                            for (let path of possiblePaths) {
                                                const res = t(path);
                                                // If i18next finds the translation, it won't return the path string
                                                if (res !== path) {
                                                    displayName = res;
                                                    break; // Found it! Stop looking.
                                                }
                                            }

                                            // Fallback description: Uses translation file if available, else YAML config
                                            const tooltipDescription = t(`verificationPage.scoreDefinitions.${taskKey}`, { defaultValue: taskData.description || '' });

                                            return (
                                                <Box key={taskKey} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox 
                                                                size="small" 
                                                                checked={isDisplayed} 
                                                                onChange={(e) => {
                                                                    const newArr = e.target.checked 
                                                                        ? [...(analysisPrefs.displayed_tasks || []), taskKey] 
                                                                        : (analysisPrefs.displayed_tasks || []).filter(item => item !== taskKey);
                                                                    handleAnalysisPrefChange('displayed_tasks', newArr);
                                                                }} 
                                                                sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: 'var(--secondary-color)' } }}
                                                            />
                                                        }
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>{displayName}</span>
                                                                <Tooltip 
                                                                    title={tooltipDescription} 
                                                                    arrow 
                                                                    placement="right"
                                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                                >
                                                                    <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.8)', cursor: 'help' } }} />
                                                                </Tooltip>
                                                            </Box>
                                                        }
                                                    />
                                                    <Tooltip 
                                                        title={!isDisplayed ? t('accountPage.analysisPrefs.enableToSetDefault') : ""} 
                                                        arrow 
                                                        placement="right"
                                                        PopperProps={{ className: 'global-dark-tooltip' }}
                                                        disableHoverListener={isDisplayed}
                                                    >
                                                        <span style={{ cursor: !isDisplayed ? 'not-allowed' : 'default' }}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch 
                                                                        className="custom-explanation-switch" 
                                                                        size="small" 
                                                                        checked={isDefault && isDisplayed} 
                                                                        disabled={!isDisplayed} 
                                                                        onChange={(e) => {
                                                                            const newArr = e.target.checked 
                                                                                ? [...(analysisPrefs.default_tasks || []), taskKey] 
                                                                                : (analysisPrefs.default_tasks || []).filter(item => item !== taskKey);
                                                                            handleAnalysisPrefChange('default_tasks', newArr);
                                                                        }} 
                                                                    />
                                                                }
                                                                label={<span style={{ color: !isDisplayed ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>{t('accountPage.analysisPrefs.enabledByDefault')}</span>}
                                                                labelPlacement="start"
                                                                sx={{ m: 0, pointerEvents: !isDisplayed ? 'none' : 'auto' }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </Box>
                                            );
                                    }) : <CircularProgress size={24} sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                                </Box>

                                {/* Meta Scores (Combined Insights) */}
                                {tasksConfig && Object.entries(tasksConfig).some(([_, cfg]) => cfg.is_meta_task) && (
                                    <Box sx={{ mt: 4 }}>
                                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
                                        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {t('accountPage.analysisPrefs.metaScoresTitle')}
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {Object.entries(tasksConfig)
                                                .filter(([_, config]) => config.is_meta_task)
                                                .map(([taskKey, taskData]) => {
                                                    const isEnabled = (analysisPrefs.enabled_meta_tasks || []).includes(taskKey);
                                                    const displayName = getTaskDisplayName(taskKey, taskData.display_name);
                                                    const tooltipDescription = t(`verificationPage.scoreDefinitions.${taskKey}`, { defaultValue: taskData.description || '' });

                                                    // --- Calculate Dependencies Before Rendering ---
                                                    const deps = taskData.depends_on || [];
                                                    const missingDeps = deps.filter(d => 
                                                        !(analysisPrefs.displayed_tasks || []).includes(d) || 
                                                        !(analysisPrefs.default_tasks || []).includes(d)
                                                    );
                                                    const hasMissingDeps = missingDeps.length > 0;
                                                    
                                                    // Build the dynamic warning message
                                                    let dependencyWarning = "";
                                                    if (hasMissingDeps) {
                                                        const missingNames = missingDeps.map(d => getTaskDisplayName(d, tasksConfig[d]?.display_name || d)).join(' & ');
                                                        dependencyWarning = t('accountPage.analysisPrefs.dependencyError', {
                                                            defaultValue: `To enable ${displayName}, you must also enable: ${missingNames}.`,
                                                            metaTask: displayName,
                                                            missingDeps: missingNames
                                                        });
                                                    }

                                                    return (
                                                        <Box key={taskKey} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.08)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>{displayName}</Typography>
                                                                <Tooltip title={tooltipDescription} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                                    <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.8)', cursor: 'help' } }} />
                                                                </Tooltip>
                                                            </Box>
                                                            {/* We wrap the control in a Tooltip. The span is required because tooltips do not fire on disabled HTML elements */}
                                                            <Tooltip 
                                                                title={hasMissingDeps ? dependencyWarning : ""} 
                                                                arrow 
                                                                placement="right" 
                                                                PopperProps={{ className: 'global-dark-tooltip' }}
                                                                disableHoverListener={!hasMissingDeps}
                                                            >
                                                                <span style={{ cursor: hasMissingDeps ? 'not-allowed' : 'default' }}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch 
                                                                                className="custom-explanation-switch" 
                                                                                size="small" 
                                                                                checked={isEnabled && !hasMissingDeps} // Force UI to off if deps are missing
                                                                                disabled={hasMissingDeps} // Disable the actual switch
                                                                                onChange={(e) => {
                                                                                    const isTurningOn = e.target.checked;
                                                                                    const newArr = isTurningOn 
                                                                                        ? [...(analysisPrefs.enabled_meta_tasks || []), taskKey] 
                                                                                        : (analysisPrefs.enabled_meta_tasks || []).filter(item => item !== taskKey);
                                                                                    handleAnalysisPrefChange('enabled_meta_tasks', newArr);
                                                                                }} 
                                                                            />
                                                                        }
                                                                        label={<span style={{ color: hasMissingDeps ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>{t('accountPage.analysisPrefs.enabledByDefault', 'Enabled by Default')}</span>}
                                                                        labelPlacement="start" 
                                                                        sx={{ m: 0, pointerEvents: hasMissingDeps ? 'none' : 'auto' }}
                                                                    />
                                                                </span>
                                                            </Tooltip>
                                                        </Box>
                                                    );
                                            })}
                                        </Box>
                                    </Box>
                                )}

                            </AccordionDetails>
                        </Accordion>

                        {/* Accordion: Advanced Options */}
                        <Accordion className="pref-accordion" expanded={expandedPanels.panel4} onChange={handleAccordionChange('panel4')} disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <SettingsSuggestIcon sx={{ color: 'var(--secondary-color)', fontSize: '1.3rem' }} />
                                    <Typography className="pref-group-title" sx={{ mb: 0, borderBottom: 'none', pb: 0 }}>
                                        {t('accountPage.analysisPrefs.advancedTitle')}
                                    </Typography>
                                    {renderInlineSaveButton('panel4')}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {/* Explanation */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    size="small" 
                                                    checked={analysisPrefs.display_explanation} 
                                                    onChange={(e) => handleAnalysisPrefChange('display_explanation', e.target.checked)} 
                                                    sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: 'var(--secondary-color)' } }}
                                                />
                                            }
                                            label={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>{t('selector.options.includeExplanation.label')}</span>}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch 
                                                    className="custom-explanation-switch" 
                                                    size="small" 
                                                    checked={analysisPrefs.default_explanation} 
                                                    disabled={!analysisPrefs.display_explanation} 
                                                    onChange={(e) => handleAnalysisPrefChange('default_explanation', e.target.checked)} 
                                                />
                                            }
                                            label={<span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>{t('accountPage.analysisPrefs.enabledByDefault')}</span>}
                                            labelPlacement="start"
                                            sx={{ m: 0 }}
                                        />
                                    </Box>

                                    {/* Regions */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    size="small" 
                                                    checked={analysisPrefs.display_regions} 
                                                    onChange={(e) => handleAnalysisPrefChange('display_regions', e.target.checked)} 
                                                    sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: 'var(--secondary-color)' } }}
                                                />
                                            }
                                            label={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>{t('selector.options.inspectableRegions.label')}</span>}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch 
                                                    className="custom-explanation-switch" 
                                                    size="small" 
                                                    checked={analysisPrefs.default_regions} 
                                                    disabled={!analysisPrefs.display_regions} 
                                                    onChange={(e) => handleAnalysisPrefChange('default_regions', e.target.checked)} 
                                                />
                                            }
                                            label={<span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>{t('accountPage.analysisPrefs.enabledByDefault')}</span>}
                                            labelPlacement="start"
                                            sx={{ m: 0 }}
                                        />
                                    </Box>

                                    {/* Auto Show Ad Tools */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <Box sx={{ flex: 1, pr: 2 }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', mb: 0.5 }}>
                                                {t('accountPage.preferences.label')}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
                                                {t('accountPage.preferences.description')}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Switch
                                                className="custom-explanation-switch"
                                                size="small"
                                                checked={isAdToolsEnabled}
                                                onChange={handleAdToolsToggle}
                                            />
                                        </Box>
                                    </Box>

                                    {/* QR Label Default Setting */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>{t('accountPage.analysisPrefs.defaultQrLabel')}</Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{t('accountPage.analysisPrefs.defaultQrLabelHelper')}</Typography>
                                        </Box>
                                        <TextField
                                            size="small"
                                            variant="outlined"
                                            value={analysisPrefs.default_qr_label ?? ''}
                                            onChange={(e) => handleAnalysisPrefChange('default_qr_label', e.target.value)}
                                            inputProps={{ maxLength: 15 }}
                                            sx={{
                                                width: '150px',
                                                '& .MuiOutlinedInput-input': { color: 'white', py: 1 },
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' },
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        {/* Accordion: General & Workflow */}
                        <Accordion className="pref-accordion" expanded={expandedPanels.panel1} onChange={handleAccordionChange('panel1')} disableGutters>
                            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <GTranslateIcon sx={{ color: 'var(--secondary-color)', fontSize: '1.3rem' }} />
                                    <Typography className="pref-group-title" sx={{ mb: 0, borderBottom: 'none', pb: 0 }}>
                                        {t('accountPage.preferences.title')}
                                    </Typography>
                                    {renderInlineSaveButton('panel1')}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {/* Default Language */}
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'rgba(255, 255, 255, 0.85)', marginBottom: '4px' }}>
                                        {t('accountPage.analysisPrefs.languageTitle')}
                                    </Typography>
                                    <Typography variant="body2" className="setting-description" sx={{ paddingLeft: '0 !important', marginTop: '0 !important', maxWidth: '350px', mb: 2 }}>
                                        {t('accountPage.analysisPrefs.languageHelper')}
                                    </Typography>
                                    <FormControl size="small" variant="outlined" sx={{ minWidth: 220 }}>
                                        <Select
                                            value={analysisPrefs.default_language ?? 'en'}
                                            onChange={(e) => handleAnalysisPrefChange('default_language', e.target.value)}
                                            inputProps={{ 'aria-label': t('accountPage.analysisPrefs.languageTitle') }}
                                            sx={{
                                                color: 'white',
                                                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' },
                                                '.MuiSvgIcon-root': { color: 'white' },
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                            MenuProps={{ PaperProps: { sx: { bgcolor: '#ffffff', color: 'rgba(0, 0, 0, 0.87)' } } }}
                                        >
                                            {LANGUAGES.map(lang => (
                                                <MenuItem key={lang.code} value={lang.code}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <img src={`/static/flags/${lang.flag}`} alt={lang.name} style={{ width: 20, height: 15, objectFit: 'cover', borderRadius: '2px' }} />
                                                        {lang.name}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Default Currency */}
                                <div className="preference-item-wrapper" style={{ marginBottom: '2rem' }}>
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'rgba(255, 255, 255, 0.85)', marginBottom: '4px' }}>
                                            {t('accountPage.preferences.currencyLabel')}
                                        </Typography>
                                        <Typography variant="body2" className="setting-description" sx={{ paddingLeft: '0 !important', marginTop: '0 !important', maxWidth: '350px' }}>
                                            {t('accountPage.preferences.currencyDescription')}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormControl size="small" variant="outlined" sx={{ minWidth: 100 }}>
                                            <Select
                                                value={currency}
                                                onChange={handleCurrencyChange}
                                                inputProps={{ 'aria-label': t('accountPage.preferences.currencyLabel') }}
                                                sx={{
                                                    color: 'white',
                                                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' },
                                                    '.MuiSvgIcon-root': { color: 'white' }
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            bgcolor: '#ffffff', color: 'rgba(0, 0, 0, 0.87)',
                                                            '& .MuiMenuItem-root': {
                                                                color: 'rgba(0, 0, 0, 0.87)',
                                                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                                                '&.Mui-selected': { backgroundColor: 'rgba(25, 118, 210, 0.08) !important', color: 'rgba(0, 0, 0, 0.87)', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.12) !important' } }
                                                            }
                                                        }
                                                    }
                                                }}
                                            >
                                                {SUPPORTED_CURRENCIES.map((code) => (
                                                    <MenuItem key={code} value={code}>
                                                        {`${code} (${CURRENCY_SYMBOLS[code] || ''})`}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </div>
                            </AccordionDetails>
                        </Accordion>
                        
                    </div>
                </Box>
                
                {/* --- 6. Danger Zone --- */}
                <Box className="account-section danger-zone">
                    <h2 className="account-section-title">{t('accountPage.dangerZone.title')}</h2>
                    <Box className="danger-item">
                        <Box>
                            <Typography variant="body1"><strong>{t('accountPage.dangerZone.deleteAccountTitle')}</strong></Typography>
                            <Typography variant="body2">{t('accountPage.dangerZone.deleteAccountDescription')}</Typography>
                        </Box>
                        <Button variant="outlined" color="error" onClick={() => setIsDeleteModalOpen(true)}>
                            {t('accountPage.dangerZone.deleteAccountButton')}
                        </Button>
                    </Box>
                </Box>

                {/* Notifications Panel */}
                {notification.message && (
                    <Alert
                        severity={notification.type}
                        sx={{
                            mb: 2,
                            mt: -2,
                            bgcolor: 'rgba(0, 0, 0, 0.2)',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            backdropFilter: 'blur(8px)',
                            borderRadius: '12px',
                            color: 'white',
                            '& .MuiAlert-icon': {
                                color: notification.type === 'success' ? '#69f0ae' : undefined,
                            },
                        }}
                    >
                        {notification.message}
                    </Alert>
                )}

                {/* Full-Screen Saving Overlay */}
                <Backdrop
                    sx={{ 
                        color: '#fff', 
                        zIndex: (theme) => theme.zIndex.drawer + 2, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 3,
                        backdropFilter: 'blur(4px)',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)'
                    }}
                    open={isSavingAnalysisPrefs || showSuccessOverlay}
                >
                    {isSavingAnalysisPrefs ? (
                        <>
                            <CircularProgress color="inherit" size={60} thickness={4} />
                            <Typography variant="h5" sx={{ fontWeight: 500, letterSpacing: '1px' }}>
                                {t('accountPage.loading.saving', 'Saving Preferences...')}
                            </Typography>
                        </>
                    ) : showSuccessOverlay ? (
                        <>
                            <CheckCircleOutlineIcon sx={{ fontSize: 90, color: 'var(--success-text-color)' }} />
                            <Typography variant="h4" sx={{ fontWeight: 600, color: 'var(--success-text-color)', letterSpacing: '1px' }}>
                                {t('accountPage.preferences.saveSuccess', 'Preferences Saved!')}
                            </Typography>
                        </>
                    ) : null}
                </Backdrop>

                {/* --- Sticky Unsaved Changes Banner --- */}
                {hasUnsavedAnalysisPrefs && (
                    <div className="unsaved-changes-banner-entrance">
                        <div className="unsaved-changes-banner" ref={saveButtonRef}>
                            <div className="unsaved-changes-content">
                                <InfoOutlinedIcon sx={{ color: 'var(--secondary-color)', mr: 1 }} />
                                <Typography variant="body2" sx={{ color: 'white', flexGrow: 1, fontWeight: 500, fontSize: '0.95rem', mr: 4 }}>
                                    {t('accountPage.unsavedChangesButtons.banner', 'You have unsaved changes')}
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    onClick={handleDiscardChanges}
                                    disabled={isSavingAnalysisPrefs}
                                    sx={{ 
                                        color: 'rgba(255,255,255,0.85)',
                                        borderColor: 'rgba(255,255,255,0.3)',
                                        textTransform: 'none', 
                                        fontWeight: 600,
                                        mr: 2, 
                                        padding: '5px 16px',
                                        '&:hover': { 
                                            color: 'white', 
                                            borderColor: 'rgba(255,255,255,0.6)',
                                            backgroundColor: 'rgba(255,255,255,0.08)' 
                                        } 
                                    }}
                                >
                                    {t('accountPage.unsavedChangesButtons.discard', 'Discard')}
                                </Button>
                                <Button 
                                    variant="contained" 
                                    onClick={handleSaveAnalysisPrefs}
                                    disabled={
                                        isSavingAnalysisPrefs || 
                                        analysisPrefs.displayed_tasks.length === 0 || 
                                        analysisPrefs.default_tasks.length === 0
                                    }
                                    sx={{ 
                                        backgroundColor: 'var(--secondary-color)', 
                                        color: 'white', 
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        boxShadow: 'none',
                                        padding: '6px 20px',
                                        '&:hover': { backgroundColor: 'var(--btn-hover-color)', boxShadow: 'none' }
                                    }}
                                >
                                    {isSavingAnalysisPrefs ? <CircularProgress size={20} color="inherit" /> : t('accountPage.unsavedChangesButtons.save', 'Save Preferences')}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

            </main>
            <Footer onOpenFaqModal={() => setIsFaqModalOpen(true)} showFaqButton={true} />
            
            {/* Delete Modal */}
            <Dialog 
                open={isDeleteModalOpen} 
                onClose={() => { 
                    setIsDeleteModalOpen(false); 
                    setDeletePassword(''); 
                    setDeleteConfirmationText(''); 
                }} 
                PaperProps={{ className: 'delete-dialog' }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backdropFilter: 'blur(6px)', /* Adds the blur effect */
                            backgroundColor: 'rgba(0, 0, 0, 0.6)' /* Darkens the background */
                        }
                    }
                }}
            >
                <DialogTitle>{t('accountPage.deleteModal.title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {isEmailUser 
                            ? t('accountPage.deleteModal.description')
                            : t('accountPage.deleteModal.descriptionSso', { keyword: confirmKeyword })
                        }
                    </DialogContentText>

                    {/* EMAIL USERS: Type Password */}
                    {isEmailUser ? (
                        <TextField 
                            autoFocus 
                            margin="dense" 
                            label={t('accountPage.deleteModal.passwordLabel')} 
                            type="password" 
                            fullWidth 
                            variant="filled" 
                            value={deletePassword} 
                            onChange={(e) => setDeletePassword(e.target.value)} 
                        />
                    ) : (
                        /* GOOGLE / SSO USERS: Type "delete" */
                        <TextField 
                            autoFocus 
                            margin="dense" 
                            label={t('accountPage.deleteModal.confirmLabel', { keyword: confirmKeyword })} 
                            type="text" 
                            fullWidth 
                            variant="filled" 
                            value={deleteConfirmationText} 
                            onChange={(e) => setDeleteConfirmationText(e.target.value)} 
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => { 
                            setIsDeleteModalOpen(false); 
                            setDeletePassword(''); 
                            setDeleteConfirmationText(''); 
                        }}
                    >
                        {t('accountPage.deleteModal.cancelButton')}
                    </Button>
                    <Button 
                        onClick={handleDeleteAccountSubmit} 
                        color="error" 
                        variant="contained" 
                        disabled={
                            isDeleting || 
                            (isEmailUser 
                                ? !deletePassword 
                                : deleteConfirmationText.trim().toLowerCase() !== confirmKeyword.toLowerCase()
                            )
                        }
                    >
                        {isDeleting ? <CircularProgress size={24} /> : t('accountPage.deleteModal.confirmButton')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Threat Radar Password Modal */}
            <Dialog 
                open={isRadarPasswordModalOpen} 
                onClose={() => { 
                    if (!isVerifyingRadarPassword) {
                        setIsRadarPasswordModalOpen(false); 
                        setRadarPasswordInput(''); 
                        setRadarPasswordError('');
                    }
                }} 
                PaperProps={{ sx: { backgroundColor: '#1e293b', color: '#f8fafc', minWidth: '400px', borderRadius: '12px', backgroundImage: 'none', border: '1px solid rgba(255,255,255,0.1)' } }}
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(0, 0, 0, 0.6)' } } }}
                style={{ zIndex: 1400 }}
            >
                <Box component="form" onSubmit={handleVerifyRadarPassword}>
                    <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: 1, color: '#f8fafc' }}>
                        <SecurityIcon sx={{ color: 'var(--secondary-color)' }} />
                        {t('accountPage.threatRadarPrefs.passwordModalTitle', 'Access Restricted')}
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 2, lineHeight: 1.6 }}>
                            {t('accountPage.threatRadarPrefs.passwordModalDesc', 'Enabling autonomous scraping incurs backend compute costs. Please enter the master password to authorize this action.')}
                        </Typography>
                        <TextField 
                            autoFocus 
                            margin="dense" 
                            label={t('accountPage.threatRadarPrefs.passwordLabel', 'Master Password')} 
                            type="password" 
                            fullWidth 
                            variant="filled" 
                            value={radarPasswordInput} 
                            onChange={(e) => setRadarPasswordInput(e.target.value)} 
                            error={!!radarPasswordError}
                            helperText={radarPasswordError}
                            sx={{
                                '& .MuiFilledInput-root': { backgroundColor: 'rgba(255,255,255,0.05)', color: 'white' },
                                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                                '& .MuiInputLabel-root.Mui-focused': { color: 'var(--secondary-color)' }
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                        <Button 
                            onClick={() => { setIsRadarPasswordModalOpen(false); setRadarPasswordInput(''); setRadarPasswordError(''); }} 
                            disabled={isVerifyingRadarPassword}
                            sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none', '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.05)' } }}
                        >
                            {t('accountPage.threatRadarPrefs.cancel', 'Cancel')}
                        </Button>
                        <Button 
                            type="submit" 
                            variant="contained" 
                            disabled={isVerifyingRadarPassword || !radarPasswordInput}
                            sx={{ ml: 2, textTransform: 'none', fontWeight: 'bold', backgroundColor: 'var(--secondary-color)', '&:hover': { backgroundColor: 'var(--btn-hover-color)' }, '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.3)' } }}
                        >
                            {isVerifyingRadarPassword ? <CircularProgress size={24} color="inherit" /> : t('accountPage.threatRadarPrefs.unlock', 'Unlock & Enable')}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            {/* Keyword Assistant Modal */}
            <Dialog 
                open={isKeywordModalOpen} 
                onClose={() => setIsKeywordModalOpen(false)}
                PaperProps={{ 
                    sx: { 
                        backgroundColor: '#1f2937', 
                        color: 'white', 
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        maxWidth: '950px',
                        width: '95%',
                        position: 'relative',
                        p: 1
                    } 
                }}
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(0, 0, 0, 0.6)' } } }}
            >
                {/* Close "X" Button in the Top Right Corner */}
                <IconButton 
                    onClick={() => setIsKeywordModalOpen(false)}
                    aria-label="close"
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 14,
                        right: 14,
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&:hover': { color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.08)' }
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.2, pb: 1, pr: 6, fontSize: '1.25rem', fontWeight: 600 }}>
                    <AutoAwesomeIcon sx={{ color: 'var(--secondary-color)' }} /> 
                    {t('accountPage.threatRadarPrefs.keywordAssistant.modalTitle', 'Keyword Assistant')}
                </DialogTitle>
                
                <DialogContent sx={{ pb: 2 }}>
                    <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2.5, fontSize: '0.92rem', lineHeight: 1.5 }}>
                        {t('accountPage.threatRadarPrefs.keywordAssistant.modalDescription', 'Describe your brand, industry, or specific threats you are worried about. We will recommend the best keywords and tasks to monitor.')}
                    </DialogContentText>
                    
                    {/* Input Box */}
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={4}
                            variant="outlined"
                            size="small"
                            placeholder={t('accountPage.threatRadarPrefs.keywordAssistant.placeholder', 'e.g., I am a luxury watch brand named (BrandName). I am worried about scammers stealing user funds.')}
                            value={trPrefs.business_profile || ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                setTrPrefs(prev => ({ ...prev, business_profile: val }));
                                if (assistantError) setAssistantError('');
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': { 
                                    color: 'white', 
                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.45)' },
                                    '&.Mui-focused fieldset': { borderColor: 'var(--secondary-color)' }
                                },
                                '& .MuiOutlinedInput-input': {
                                    textOverflow: 'ellipsis'
                                }
                            }}
                        />
                        {assistantError && (
                            <Typography variant="caption" sx={{ color: '#F44336', display: 'block', mt: 1, fontWeight: 500 }}>
                                {assistantError}
                            </Typography>
                        )}
                    </Box>
                    
                    {/* Centered Generate Button & Loading Text */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                        <Button 
                            variant="contained" 
                            onClick={handleGenerateKeywords}
                            disabled={isGeneratingKeywords || !(trPrefs.business_profile || '').trim()}
                            sx={{ 
                                backgroundColor: 'var(--secondary-color)', 
                                color: 'white',
                                fontWeight: 'bold',
                                textTransform: 'none',
                                minWidth: '200px',
                                px: 3,
                                py: 0.75,
                                boxShadow: 'none',
                                transition: 'background-color 0.3s ease, transform 0.1s ease',
                                '&:hover': {
                                    backgroundColor: 'var(--btn-hover-color)',
                                    boxShadow: 'none'
                                },
                                '&:active': {
                                    transform: 'scale(0.98)'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.12) !important',
                                    color: 'rgba(255, 255, 255, 0.2) !important',
                                    cursor: 'not-allowed !important'
                                }
                            }}
                        >
                            {isGeneratingKeywords ? <CircularProgress size={20} color="inherit" /> : t('accountPage.threatRadarPrefs.keywordAssistant.generateButton', 'Generate Recommendations')}
                        </Button>
                        
                        <Box sx={{ minHeight: '24px', mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {showSlowLoadingMsg && (
                                <Typography variant="caption" sx={{ color: 'var(--secondary-color)', fontStyle: 'italic', lineHeight: 1.2, textAlign: 'center' }}>
                                    {t('accountPage.threatRadarPrefs.keywordAssistant.loadingMessage', 'Analyzing industry threats using advanced AI... This may take 30-60 seconds.')}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Generated Keyword Chips */}
                    {suggestedAiKeywords.length > 0 && (
                        <Box sx={{ bgcolor: 'rgba(0,0,0,0.25)', p: 2, borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                                    {t('accountPage.threatRadarPrefs.keywordAssistant.selectLabel', 'Select the keywords you want to track:')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: selectedAiKeywords.length >= availableSlots ? 'var(--secondary-color)' : 'rgba(255,255,255,0.5)' }}>
                                    {t('accountPage.threatRadarPrefs.keywordAssistant.slotsSelected', '{{selected}}/{{available}} available slots selected', { selected: selectedAiKeywords.length, available: availableSlots })}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {suggestedAiKeywords.map((kw, idx) => {
                                    const isSelected = selectedAiKeywords.includes(kw);
                                    const isSlotFull = !isSelected && selectedAiKeywords.length >= availableSlots;
                                    const aiReason = aiKeywordReasons[kw];

                                    const actionText = isSelected 
                                        ? t('accountPage.threatRadarPrefs.keywordAssistant.clickToDeselect', 'Click to deselect') 
                                        : t('accountPage.threatRadarPrefs.keywordAssistant.clickToSelect', 'Click to select');

                                    const chipTooltip = isSlotFull 
                                        ? t('maxKeywords', { max: MAX_TR_KEYWORDS, defaultValue: `Maximum of ${MAX_TR_KEYWORDS} keywords allowed` })
                                        : (
                                            <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: '250px' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--secondary-color)' }}>{actionText}</Typography>
                                                {aiReason && (
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontStyle: 'italic', lineHeight: 1.3 }}>
                                                        {aiReason}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );

                                    return (
                                        <Tooltip key={idx} title={chipTooltip} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                            <span>
                                                <Chip
                                                    label={kw}
                                                    onClick={() => toggleKeywordSelection(kw)}
                                                    disabled={isSlotFull}
                                                    icon={isSelected ? <CheckIcon style={{ fontSize: '15px', color: 'white' }} /> : undefined}
                                                    sx={{ 
                                                        cursor: isSlotFull ? 'not-allowed' : 'pointer',
                                                        backgroundColor: isSelected 
                                                            ? 'var(--secondary-color)' 
                                                            : 'rgba(255, 255, 255, 0.04)',
                                                        color: isSelected 
                                                            ? '#ffffff' 
                                                            : 'rgba(255, 255, 255, 0.75)',
                                                        borderColor: isSelected 
                                                            ? 'var(--secondary-color)' 
                                                            : 'rgba(255, 255, 255, 0.25)',
                                                        borderWidth: '1px',
                                                        borderStyle: 'solid',
                                                        fontWeight: isSelected ? 600 : 400,
                                                        transition: 'all 0.15s ease-in-out',
                                                        '&:hover': {
                                                            backgroundColor: isSelected 
                                                                ? 'var(--btn-hover-color)' 
                                                                : 'rgba(255, 255, 255, 0.1)',
                                                            color: 'white'
                                                        },
                                                        '&.Mui-disabled': {
                                                            opacity: 0.35,
                                                            cursor: 'not-allowed',
                                                            borderColor: 'rgba(255, 255, 255, 0.15)'
                                                        }
                                                    }}
                                                />
                                            </span>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {/* Generated Task Chips */}
                    {suggestedAiTasks.length > 0 && (
                        <Box sx={{ bgcolor: 'rgba(0,0,0,0.25)', p: 2, borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                                    {t('accountPage.threatRadarPrefs.keywordAssistant.selectTasksLabel', 'Recommended Analysis Tasks:')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {suggestedAiTasks.map((taskKey, idx) => {
                                    const isSelected = selectedAiTasks.includes(taskKey);
                                    const taskData = tasksConfig?.[taskKey] || {};
                                    const displayName = getTaskDisplayName(taskKey, taskData.display_name || taskKey);
                                    const aiReason = aiTaskReasons[taskKey];
                                    
                                    const actionText = isSelected 
                                        ? t('accountPage.threatRadarPrefs.keywordAssistant.clickToDeselect', 'Click to deselect') 
                                        : t('accountPage.threatRadarPrefs.keywordAssistant.clickToSelect', 'Click to select');
                                        
                                    const chipTooltip = (
                                        <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: '250px' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--secondary-color)' }}>{actionText}</Typography>
                                            {aiReason && (
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontStyle: 'italic', lineHeight: 1.3 }}>
                                                    {aiReason}
                                                </Typography>
                                            )}
                                        </Box>
                                    );

                                    return (
                                        <Tooltip key={`task-${idx}`} title={chipTooltip} arrow placement="top" PopperProps={{ className: 'global-dark-tooltip' }}>
                                            <span>
                                                <Chip
                                                    label={displayName}
                                                    onClick={() => toggleTaskSelection(taskKey)}
                                                    icon={isSelected ? <CheckIcon style={{ fontSize: '15px', color: 'white' }} /> : undefined}
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        backgroundColor: isSelected 
                                                            ? 'var(--secondary-color)' 
                                                            : 'rgba(255, 255, 255, 0.04)',
                                                        color: isSelected 
                                                            ? '#ffffff' 
                                                            : 'rgba(255, 255, 255, 0.75)',
                                                        borderColor: isSelected 
                                                            ? 'var(--secondary-color)' 
                                                            : 'rgba(255, 255, 255, 0.25)',
                                                        borderWidth: '1px',
                                                        borderStyle: 'solid',
                                                        fontWeight: isSelected ? 600 : 400,
                                                        transition: 'all 0.15s ease-in-out',
                                                        '&:hover': {
                                                            backgroundColor: isSelected 
                                                                ? 'var(--btn-hover-color)' 
                                                                : 'rgba(255, 255, 255, 0.1)',
                                                            color: 'white'
                                                        }
                                                    }}
                                                />
                                            </span>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                
                {(suggestedAiKeywords.length > 0 || suggestedAiTasks.length > 0) && (
                    <DialogActions sx={{ p: 2.5, pt: 0, justifyContent: 'flex-end' }}>
                        <Button 
                            onClick={handleApplySuggestedKeywords} 
                            variant="contained"
                            disabled={selectedAiKeywords.length === 0 && selectedAiTasks.length === 0}
                            sx={{ 
                                backgroundColor: 'var(--secondary-color)', 
                                color: 'white', 
                                fontWeight: 'bold',
                                textTransform: 'none',
                                px: 2.5,
                                boxShadow: 'none',
                                transition: 'background-color 0.3s ease, transform 0.1s ease',
                                '&:hover': { 
                                    backgroundColor: 'var(--btn-hover-color)',
                                    boxShadow: 'none'
                                },
                                '&:active': {
                                    transform: 'scale(0.98)'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.12) !important',
                                    color: 'rgba(255, 255, 255, 0.2) !important',
                                    cursor: 'not-allowed !important'
                                }
                            }}
                        >
                            {t('accountPage.threatRadarPrefs.keywordAssistant.addSelectedButton', { 
                                keywords: selectedAiKeywords.length, 
                                tasks: selectedAiTasks.length 
                            })}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            {/* Native Browser Back Button Confirmation Dialog */}
            {ReactDOM.createPortal(
                <div className={`confirmation-dialog-overlay ${isManualUnsavedDialogOpen ? 'open' : ''}`} onClick={handleStayOnPage}>
                    {isManualUnsavedDialogOpen && (
                        <div className="confirmation-dialog-content" onClick={(e) => e.stopPropagation()}>
                            <IconButton aria-label="close" onClick={handleStayOnPage} className="dialog-close-button">
                                <CloseIcon />
                            </IconButton>
                            <h2 className="confirmation-dialog-title">{t('bulkAnalysisPage.dialogs.unsavedChanges.title')}</h2>
                            <div className="confirmation-dialog-body">
                                <p className="confirmation-dialog-text">{t('bulkAnalysisPage.dialogs.unsavedChanges.description')}</p>
                            </div>
                            <div className="confirmation-dialog-actions">
                                <Button onClick={handleStayOnPage} variant="outlined">
                                    {t('bulkAnalysisPage.dialogs.unsavedChanges.cancelButton')}
                                </Button>
                                <Button onClick={handleManualLeavePage} color="error" variant="contained">
                                    {t('bulkAnalysisPage.dialogs.unsavedChanges.confirmButton')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>,
                document.body
            )}
            
        </div>
    );
};

// We wrap the entire page layout in the Provider to guarantee that Header inherits its context
const AccountPage = () => (
    <UnsavedChangesProvider>
        <AccountPageContent />
    </UnsavedChangesProvider>
);

export default AccountPage;